import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AgentProductSelector } from "./AgentProductSelector";
import { AgentConfigSection } from "./AgentConfigSection";
import { CommentReplySection } from "./sections/CommentReplySection";
import { KnowledgeBaseSection } from "./KnowledgeBaseSection";
import { AgentCardInfo } from "./AgentCardInfo";
import { BusinessHoursSelector, DaySchedule, validateBusinessHours } from "./BusinessHoursSelector";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion } from "@/components/ui/accordion";
import { Slider } from "@/components/ui/slider";
import {
  User,
  Building2,
  Settings,
  Eye,
  Plus,
  X,
  Sparkles,
  HelpCircle,
  Info,
  Lightbulb,
  FileUp,
  Trash2,
  FileText,
  Link2,
  Copy,
  Download,
  Upload,
  MoreHorizontal,
  FileSpreadsheet,
  AlertTriangle,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  MAX_CATALOG_TRIGGER_PHRASES,
  normalizeCatalogTriggerPhrases,
} from "@/lib/catalog-trigger-phrases";
import {
  type AgentConfigSnapshot,
  exportRowsToCsv,
  exportRowsToXlsx,
  formatRowsAsPlainText,
  generateAgentConfigTemplateCsv,
  generateAgentConfigTemplateXlsx,
  snapshotToRows,
} from "@/lib/agent-config-export";
import { AgentConfigImportDialog } from "./AgentConfigImportDialog";
import { cn } from "@/lib/utils";
import { fetchAllPages, insertInBatches } from "@/lib/supabase-pagination";
import { DynamicListInput } from "./sections/DynamicListInput";
import { AddressManager, AddressItem } from "./sections/AddressManager";
import { HandoffRuleInput, HandoffRule } from "./sections/HandoffRuleInput";
import { AgentEscalationRuleInput, AgentEscalationRule } from "./sections/AgentEscalationRuleInput";
import { PaymentSection } from "./sections/PaymentSection";
import { DeliverySection } from "./sections/DeliverySection";
import { useGoogleCalendars } from "@/hooks/useGoogleCalendars";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { isGcalMultiCalendarEnabled } from "@/lib/gcal-feature-flags";

/** Limite de produto (UI): máximo de agendas no allowlist do agente. */
const MAX_AGENT_ALLOWED_CALENDARS = 2;

const formSchema = z.object({
  name: z.string().min(2, "Nome deve ter pelo menos 2 caracteres"),
  display_name: z.string().optional(),
  greeting_message: z.string().max(500, "Saudação deve ter no máximo 500 caracteres").optional(),
  role: z.enum(["vendas", "suporte", "sdr", "agendamentos"]),
  tone: z.string().optional(),
  language: z.string().default("pt-BR"),
  timezone: z.string().default("America/Sao_Paulo"),
  is_active: z.boolean(),
  restrict_to_business_hours: z.boolean().default(false),
  google_calendar_enabled: z.boolean().default(false),
  google_calendar_sync_calendar_id: z.string().nullable().optional(),
  appointment_duration: z.number().min(15).max(480).default(60),
  max_appointment_options: z.number().int().min(1).max(5).default(3),
  scheduling_start_offset_days: z.number().int().min(0).max(60).default(0),
  scheduling_search_days: z.number().int().min(1).max(30).default(5),
  allow_overlapping_appointments: z.boolean().default(false),
  address_enabled: z.boolean().default(false),
  phone_numbers: z.array(z.string()).default([]),
  phone_numbers_enabled: z.boolean().default(false),
  social_media: z.object({
    instagram: z.string().optional(),
    facebook: z.string().optional(),
    linkedin: z.string().optional(),
    twitter: z.string().optional(),
    whatsapp: z.string().optional(),
    email: z.string().optional(),
    website: z.string().optional()
  }).optional(),
  social_media_enabled: z.boolean().default(false),
  comment_website_link_placement: z.enum(["public", "dm_only"]).default("public"),
  comment_product_link_placement: z.enum(["public", "dm_only"]).default("dm_only"),
  comment_reply_mode: z.enum(["ai_decides", "always_both", "public_only"]).default("ai_decides"),
  establishment_name: z.string().optional(),
  can_schedule: z.boolean().default(true),
  can_reschedule: z.boolean().default(true),
  can_cancel: z.boolean().default(true),
  // New fields
  cnpj: z.string().optional(),
  other_social_media: z.string().optional(),
  payment_methods: z.array(z.string()).default([]),
  payment_installments: z.string().optional(),
  show_prices_to_customer: z.boolean().default(true),
  price_disclosure_mode: z.enum(["free", "value_first", "never"]).default("free"),
  price_disclosure_condition: z.string().max(500).optional().nullable(),
  delivery_info: z.string().optional(),
  delivery_enabled: z.boolean().default(false),
  about_company: z.string().max(2000, "Limite de 2000 caracteres").optional(),
  target_audience: z.string().max(600, "Limite de 600 caracteres").optional(),
  value_proposition: z.string().max(600, "Limite de 600 caracteres").optional(),
  agent_goal: z.string().max(400, "Limite de 400 caracteres").optional(),
  desired_next_step: z.string().max(400, "Limite de 400 caracteres").optional(),
  service_area: z.string().max(500, "Limite de 500 caracteres").optional(),
  limitations: z.string().max(600, "Limite de 600 caracteres").optional(),
  business_scope: z.string().max(800, "Limite de 800 caracteres").optional(),
  out_of_scope_behavior: z.string().max(800, "Limite de 800 caracteres").optional(),
  policies_summary: z.string().max(1000, "Limite de 1000 caracteres").optional(),
  required_customer_data: z.string().max(600, "Limite de 600 caracteres").optional(),
  llm_temperature: z.number().min(0).max(1).default(0.3),
});
interface AgentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Chamado após salvar com sucesso (o diálogo permanece aberto). Atualize o agente selecionado na página pai. */
  onSaved?: (agentId: string) => void;
  agent?: any;
  workspaceId: string;
}
const toneOptions = [{
  value: "formal e objetivo",
  label: "Formal",
  icon: "🎩"
}, {
  value: "profissional e amigável",
  label: "Profissional",
  icon: "💼"
}, {
  value: "cordial e animado",
  label: "Cordial",
  icon: "😊"
}, {
  value: "descontraído e casual",
  label: "Descontraído",
  icon: "🌴"
}, {
  value: "consultivo e educativo",
  label: "Consultivo",
  icon: "📚"
}, {
  value: "empático e acolhedor",
  label: "Empático",
  icon: "💜"
}, {
  value: "persuasivo e assertivo",
  label: "Assertivo",
  icon: "🎯"
}, {
  value: "energético e motivador",
  label: "Energético",
  icon: "⚡"
}];
const agentRoleLabels: Record<string, string> = {
  vendas: "Vendas",
  suporte: "Suporte",
  sdr: "SDR",
  agendamentos: "Agendamentos",
};

const CREATIVITY_HINTS: Record<number, string> = {
  0: "🎯 Respostas bem diretas, seguindo exatamente o que você configurou.",
  10: "📋 Pouca variação no texto. Ideal para respostas técnicas e padronizadas.",
  20: "📐 Foco total nas regras, com linguagem bem consistente.",
  30: "⚖️ Equilíbrio entre precisão e naturalidade na conversa.",
  40: "💬 Tom conversacional, ainda alinhado às suas diretrizes.",
  50: "✨ Formulações mais naturais, com alguma liberdade no texto.",
  60: "🌟 Respostas com personalidade, sem perder o objetivo do atendimento.",
  70: "🎨 Mais variedade nas frases e nas sugestões ao cliente.",
  80: "🔥 Criativo e espontâneo. Boas alternativas de abordagem.",
  90: "💡 Muita liberdade na escrita. Vale revisar as conversas com frequência.",
  100: "🚀 Máxima criatividade. Melhor para brainstorms do que para fluxos rígidos.",
};

function getCreativityHint(temperature: number | undefined): string {
  const pct = Math.round((temperature ?? 0.3) * 10) * 10;
  return CREATIVITY_HINTS[pct] ?? CREATIVITY_HINTS[30];
}

export function AgentDialog({
  open,
  onOpenChange,
  onSaved,
  agent,
  workspaceId
}: AgentDialogProps) {
  const {
    toast
  } = useToast();
  const { user } = useAuth();
  const { workspaceMetadata } = useWorkspace(user?.id);
  const { connections: gcalConnections, isLoading: gcalLoading } = useGoogleCalendars();
  const gcalMultiEnabled = isGcalMultiCalendarEnabled(workspaceMetadata);
  const allSyncCalendars = gcalConnections.flatMap((conn) => conn.syncCalendars);
  const enabledSyncCalendars = allSyncCalendars.filter((sc) => sc.is_enabled);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  /** Agendas Google que este agente pode usar (allowlist multi-agenda). */
  const [allowedCalendarIds, setAllowedCalendarIds] = useState<string[]>([]);
  const allowedCalendarsOverLimit = allowedCalendarIds.length > MAX_AGENT_ALLOWED_CALENDARS;
  /** Resumo multi-agenda dos serviços vinculados (só com flag on). */
  const [linkedServiceCalendars, setLinkedServiceCalendars] = useState<
    Array<{ id: string; name: string; google_calendar_sync_calendar_id: string | null }>
  >([]);
  const [businessHours, setBusinessHours] = useState<DaySchedule[]>([]);
  const [followUpEnabled, setFollowUpEnabled] = useState(false);
  const [followUpInterval, setFollowUpInterval] = useState(60);
  const [followUpMaxAttempts, setFollowUpMaxAttempts] = useState(3);
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [reminderTimeBefore, setReminderTimeBefore] = useState(60);
  const [showPromptPreview, setShowPromptPreview] = useState(false);

  // Human handoff
  const [humanHandoffEnabled, setHumanHandoffEnabled] = useState(true);
  const [humanHandoffRules, setHumanHandoffRules] = useState<string[]>([]);
  const [humanHandoffRulesV2, setHumanHandoffRulesV2] = useState<HandoffRule[]>([]);

  // Agent-to-agent escalation
  const [agentEscalationRulesV2, setAgentEscalationRulesV2] = useState<AgentEscalationRule[]>([]);

  // Catalog (MinIO)
  const [catalogEnabled, setCatalogEnabled] = useState(false);
  const [catalogFileUrl, setCatalogFileUrl] = useState<string | null>(null);
  const [catalogFileName, setCatalogFileName] = useState<string | null>(null);
  const [catalogStorageProvider, setCatalogStorageProvider] = useState<string | null>(null);
  const [catalogBucket, setCatalogBucket] = useState<string | null>(null);
  const [catalogObjectKey, setCatalogObjectKey] = useState<string | null>(null);
  const [catalogMimeType, setCatalogMimeType] = useState<string | null>(null);
  const [catalogFileSize, setCatalogFileSize] = useState<number | null>(null);
  const [catalogPublicUrl, setCatalogPublicUrl] = useState<string | null>(null);
  const [catalogTriggerPhrases, setCatalogTriggerPhrases] = useState<string[]>([]);
  const [isUploadingCatalog, setIsUploadingCatalog] = useState(false);

  // Dynamic lists
  const [qualificationQuestions, setQualificationQuestions] = useState<string[]>([]);
  const [businessRulesList, setBusinessRulesList] = useState<string[]>([]);
  const [agentInstructions, setAgentInstructions] = useState<string[]>([]);
  const [addresses, setAddresses] = useState<AddressItem[]>([]);
  const [faqItems, setFaqItems] = useState<{ question: string; answer: string }[]>([]);
  const [kbOverridesFaq, setKbOverridesFaq] = useState(false);
  const [kbDocCount, setKbDocCount] = useState(0);
  const [businessCategory, setBusinessCategory] = useState<string>("other");
  const [businessTags, setBusinessTags] = useState<string[]>([]);
  const initKeyRef = useRef<string | null>(null);
  /** Snapshot serializado da última versão "salva" ou carregada; null = ainda não pronto para comparar */
  const baselineRef = useRef<string | null>(null);
  const dirtyStateRef = useRef<Record<string, unknown>>({});
  const [unsavedCloseOpen, setUnsavedCloseOpen] = useState(false);
  const [importConfigOpen, setImportConfigOpen] = useState(false);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      display_name: "",
      greeting_message: "",
      role: "vendas",
      tone: "profissional e amigável",
      language: "pt-BR",
      timezone: "America/Sao_Paulo",
      is_active: true,
      restrict_to_business_hours: false,
      google_calendar_enabled: false,
      google_calendar_sync_calendar_id: null,
      appointment_duration: 60,
      max_appointment_options: 3,
      scheduling_start_offset_days: 0,
      scheduling_search_days: 5,
      allow_overlapping_appointments: false,
      address_enabled: false,
      phone_numbers: [],
      phone_numbers_enabled: false,
      social_media: {
        instagram: "",
        facebook: "",
        linkedin: "",
        twitter: "",
        whatsapp: "",
        email: "",
        website: ""
      },
      social_media_enabled: false,
      comment_website_link_placement: "public",
      comment_product_link_placement: "dm_only",
      comment_reply_mode: "ai_decides",
      establishment_name: "",
      can_schedule: true,
      can_reschedule: true,
      can_cancel: true,
      cnpj: "",
      other_social_media: "",
      payment_methods: [],
      payment_installments: "",
      show_prices_to_customer: true,
      price_disclosure_mode: "free",
      price_disclosure_condition: "",
      delivery_info: "",
      delivery_enabled: false,
      about_company: "",
      target_audience: "",
      value_proposition: "",
      agent_goal: "",
      desired_next_step: "",
      service_area: "",
      limitations: "",
      business_scope: "",
      out_of_scope_behavior: "",
      policies_summary: "",
      required_customer_data: "",
      llm_temperature: 0.3,
    }
  });
  const selectedSyncCalendarId = form.watch("google_calendar_sync_calendar_id");
  const selectedSyncCalendarLabel = (() => {
    const sc = enabledSyncCalendars.find((c) => c.id === selectedSyncCalendarId);
    if (!sc) return null;
    return `${sc.display_name || sc.calendar_id}${sc.is_primary ? " (primária)" : ""}`;
  })();
  useEffect(() => {
    if (!open) {
      initKeyRef.current = null;
      baselineRef.current = null;
      return;
    }
    const currentInitKey = agent?.id ?? "new";
    if (initKeyRef.current === currentInitKey) {
      return;
    }
    initKeyRef.current = currentInitKey;
    if (agent) {
      form.reset({
        name: agent.name,
        display_name: agent.display_name || "",
        greeting_message: agent.greeting_message || "",
        role: agent.role as any,
        tone: agent.tone || "profissional e amigável",
        language: agent.language || "pt-BR",
        timezone: agent.timezone || "America/Sao_Paulo",
        is_active: agent.is_active ?? true,
        restrict_to_business_hours: agent.restrict_to_business_hours ?? false,
        google_calendar_enabled: agent.google_calendar_enabled ?? false,
        google_calendar_sync_calendar_id: agent.google_calendar_sync_calendar_id ?? null,
        appointment_duration: agent.appointment_duration || 60,
        max_appointment_options: (agent as any).max_appointment_options ?? 3,
        scheduling_start_offset_days: Number((agent as any).scheduling_start_offset_days) || 0,
        scheduling_search_days: Number((agent as any).scheduling_search_days) || 5,
        allow_overlapping_appointments: agent.allow_overlapping_appointments ?? false,
        address_enabled: agent.address_enabled ?? false,
        phone_numbers: agent.phone_numbers || [],
        phone_numbers_enabled: agent.phone_numbers_enabled ?? false,
        social_media: agent.social_media || {
          instagram: "",
          facebook: "",
          linkedin: "",
          twitter: "",
          whatsapp: "",
          email: "",
          website: ""
        },
        social_media_enabled: agent.social_media_enabled ?? false,
        comment_website_link_placement: agent.comment_website_link_placement === "dm_only" ? "dm_only" : "public",
        comment_product_link_placement: agent.comment_product_link_placement === "public" ? "public" : "dm_only",
        comment_reply_mode: agent.comment_reply_mode === "always_both" || agent.comment_reply_mode === "public_only"
          ? agent.comment_reply_mode
          : "ai_decides",
        establishment_name: agent.establishment_name || "",
        can_schedule: agent.can_schedule ?? true,
        can_reschedule: agent.can_reschedule ?? true,
        can_cancel: agent.can_cancel ?? true,
        cnpj: agent.cnpj || "",
        other_social_media: agent.other_social_media || "",
        payment_methods: agent.payment_methods || [],
        payment_installments: agent.payment_installments || "",
        show_prices_to_customer: agent.show_prices_to_customer ?? true,
        price_disclosure_mode: (agent.price_disclosure_mode as any) ?? (agent.show_prices_to_customer === false ? "never" : "free"),
        price_disclosure_condition: agent.price_disclosure_condition ?? "",
        delivery_info: agent.delivery_info || "",
        delivery_enabled: agent.delivery_enabled ?? false,
        about_company: agent.about_company || "",
        target_audience: agent.target_audience || "",
        value_proposition: agent.value_proposition || "",
        agent_goal: agent.agent_goal || "",
        desired_next_step: agent.desired_next_step || "",
        service_area: agent.service_area || "",
        limitations: agent.limitations || "",
        business_scope: (agent as any).business_scope || "",
        out_of_scope_behavior: (agent as any).out_of_scope_behavior || "",
        policies_summary: agent.policies_summary || "",
        required_customer_data: agent.required_customer_data || "",
        llm_temperature: typeof agent.llm_temperature === "number" ? agent.llm_temperature : 0.3,
      });
      setQualificationQuestions(agent.qualification_questions || []);
      setBusinessRulesList(agent.business_rules_list || []);
      setAgentInstructions(agent.agent_instructions || []);
      setAddresses(agent.addresses || []);
      setFaqItems((agent as any).faq_items || []);
      setKbOverridesFaq(Boolean((agent as any).kb_overrides_faq));
      setKbDocCount(0);
      setBusinessCategory(agent.business_category || "other");
      setBusinessTags(agent.business_tags || []);
      setHumanHandoffEnabled(agent.human_handoff_enabled ?? true);
      setHumanHandoffRules(agent.human_handoff_rules || []);
      setHumanHandoffRulesV2((agent as any).human_handoff_rules_v2 || []);
      setAgentEscalationRulesV2((agent as any).agent_escalation_rules_v2 || []);
      setCatalogEnabled(agent.catalog_file_enabled ?? false);
      setCatalogFileUrl(agent.catalog_file_url || null);
      const a: any = agent as any;
      setCatalogStorageProvider(a.catalog_storage_provider || null);
      setCatalogBucket(a.catalog_bucket || null);
      setCatalogObjectKey(a.catalog_object_key || null);
      setCatalogMimeType(a.catalog_mime_type || null);
      setCatalogFileSize(a.catalog_file_size_bytes ?? null);
      setCatalogPublicUrl(a.catalog_public_url || null);
      setCatalogFileName(
        a.catalog_file_name ||
        (agent.catalog_file_url ? agent.catalog_file_url.split('/').pop() || 'catalog.pdf' : null)
      );
      setCatalogTriggerPhrases(
        normalizeCatalogTriggerPhrases((agent as { catalog_trigger_phrases?: string[] }).catalog_trigger_phrases),
      );
      fetchAgentProducts();
    } else {
      form.reset({
        name: "",
        display_name: "",
        greeting_message: "",
        role: "vendas",
        tone: "profissional e amigável",
        language: "pt-BR",
        timezone: "America/Sao_Paulo",
        is_active: true,
        restrict_to_business_hours: false,
        google_calendar_enabled: false,
        google_calendar_sync_calendar_id: null,
        appointment_duration: 60,
        max_appointment_options: 3,
        scheduling_start_offset_days: 0,
        scheduling_search_days: 5,
        allow_overlapping_appointments: false,
        address_enabled: false,
        phone_numbers: [],
        phone_numbers_enabled: false,
        social_media: {
          instagram: "",
          facebook: "",
          linkedin: "",
          twitter: "",
          whatsapp: "",
          email: "",
          website: ""
        },
        social_media_enabled: false,
        comment_website_link_placement: "public",
        comment_product_link_placement: "dm_only",
        comment_reply_mode: "ai_decides",
        establishment_name: "",
        can_schedule: true,
        can_reschedule: true,
        can_cancel: true,
        cnpj: "",
        other_social_media: "",
        payment_methods: [],
        payment_installments: "",
        show_prices_to_customer: true,
        price_disclosure_mode: "free",
        price_disclosure_condition: "",
        delivery_info: "",
        delivery_enabled: false,
        about_company: "",
        target_audience: "",
        value_proposition: "",
        agent_goal: "",
        desired_next_step: "",
        service_area: "",
        limitations: "",
        business_scope: "",
        out_of_scope_behavior: "",
        policies_summary: "",
        required_customer_data: "",
        llm_temperature: 0.3,
      });
      setSelectedProductIds([]);
      setAllowedCalendarIds([]);
      setLinkedServiceCalendars([]);
      setBusinessHours([]);
      setFollowUpEnabled(false);
      setFollowUpInterval(60);
      setFollowUpMaxAttempts(3);
      setReminderEnabled(false);
      setReminderTimeBefore(60);
      setQualificationQuestions([]);
      setBusinessRulesList([]);
      setAgentInstructions([]);
      setAddresses([]);
      setFaqItems([]);
      setKbOverridesFaq(false);
      setKbDocCount(0);
      setBusinessCategory("other");
      setBusinessTags([]);
      setHumanHandoffEnabled(true);
      setHumanHandoffRules([]);
      setHumanHandoffRulesV2([]);
      setCatalogEnabled(false);
      setCatalogFileUrl(null);
      setCatalogFileName(null);
      setCatalogTriggerPhrases([]);
      setTimeout(() => {
        baselineRef.current = JSON.stringify(dirtyStateRef.current);
      }, 0);
    }
  }, [agent, open]);
  const fetchAgentProducts = async () => {
    if (!agent) return;
    const agentProducts = await fetchAllPages<{ product_id: string }>((from, to) =>
      supabase
        .from("agent_products")
        .select("product_id")
        .eq("agent_id", agent.id)
        .range(from, to),
    );
    setSelectedProductIds(agentProducts.map((ap) => ap.product_id));

    const { data: allowedRows } = await supabase
      .from("agent_google_calendars")
      .select("google_calendar_sync_calendar_id")
      .eq("agent_id", agent.id);
    const fromTable = (allowedRows ?? []).map((r) => r.google_calendar_sync_calendar_id);
    // Compat: allowlist vazia + default set → tratar default como única allowed
    if (
      fromTable.length === 0 &&
      agent.google_calendar_sync_calendar_id
    ) {
      setAllowedCalendarIds([agent.google_calendar_sync_calendar_id]);
    } else {
      setAllowedCalendarIds(fromTable);
    }

    const {
      data: hours
    } = await supabase.from("agent_business_hours").select("*").eq("agent_id", agent.id).order("day_of_week");
    if (hours) {
      setBusinessHours(hours);
    }
    const {
      data: followUpConfig
    } = await supabase.from("agent_followup_config").select("enabled, interval_minutes, max_attempts").eq("agent_id", agent.id).maybeSingle();
    setFollowUpEnabled(followUpConfig?.enabled || false);
    setFollowUpInterval(followUpConfig?.interval_minutes || 60);
    setFollowUpMaxAttempts(followUpConfig?.max_attempts || 3);
    setTimeout(() => {
      baselineRef.current = JSON.stringify(dirtyStateRef.current);
    }, 0);
  };

  // Resumo: serviços vinculados × agenda própria (multi-agenda)
  useEffect(() => {
    if (!open || !gcalMultiEnabled || selectedProductIds.length === 0) {
      setLinkedServiceCalendars([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("products")
        .select("id, name, type, google_calendar_sync_calendar_id")
        .eq("workspace_id", workspaceId)
        .eq("type", "service")
        .in("id", selectedProductIds);
      if (!cancelled) {
        setLinkedServiceCalendars(
          (data ?? []).map((p) => ({
            id: p.id,
            name: p.name,
            google_calendar_sync_calendar_id: p.google_calendar_sync_calendar_id ?? null,
          })),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, gcalMultiEnabled, selectedProductIds, workspaceId]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      if (
        values.google_calendar_enabled &&
        gcalMultiEnabled &&
        !values.google_calendar_sync_calendar_id
      ) {
        form.setError("google_calendar_sync_calendar_id", {
          type: "manual",
          message: "Selecione a agenda padrão (fallback) deste agente.",
        });
        toast({
          title: "Agenda padrão obrigatória",
          description:
            "Com Google Calendar ativo, escolha a agenda padrão do agente. Serviços com profissionais diferentes devem ter a própria agenda em Produtos/Serviços.",
          variant: "destructive",
        });
        return;
      }

      // Allowlist final (antes de qualquer write) — evita agente salvo com allowlist inconsistente.
      let allowlistIdsToSave: string[] | null = null;
      if (
        values.google_calendar_enabled &&
        gcalMultiEnabled &&
        values.google_calendar_sync_calendar_id
      ) {
        if (allowedCalendarIds.length === 0) {
          toast({
            title: "Selecione as agendas do agente",
            description:
              "Marque pelo menos uma agenda que este agente pode usar. Agendas internas (gestão) devem ficar de fora.",
            variant: "destructive",
          });
          return;
        }
        if (allowedCalendarIds.length > MAX_AGENT_ALLOWED_CALENDARS) {
          toast({
            title: "Muitas agendas selecionadas",
            description: `Por enquanto você pode selecionar no máximo ${MAX_AGENT_ALLOWED_CALENDARS} agendas. Desmarque as extras antes de salvar.`,
            variant: "destructive",
          });
          return;
        }
        if (!allowedCalendarIds.includes(values.google_calendar_sync_calendar_id)) {
          form.setError("google_calendar_sync_calendar_id", {
            type: "manual",
            message: "A agenda padrão deve estar entre as agendas permitidas.",
          });
          toast({
            title: "Agenda padrão inválida",
            description:
              "A agenda padrão precisa estar marcada em “Agendas que este agente pode usar”.",
            variant: "destructive",
          });
          return;
        }
        const defaultEnabled = enabledSyncCalendars.some(
          (sc) => sc.id === values.google_calendar_sync_calendar_id,
        );
        if (!defaultEnabled) {
          form.setError("google_calendar_sync_calendar_id", {
            type: "manual",
            message: "A agenda padrão precisa estar habilitada em Integrações.",
          });
          toast({
            title: "Agenda padrão desabilitada",
            description:
              "Reative a agenda em Integrações ou escolha outra agenda habilitada no allowlist.",
            variant: "destructive",
          });
          return;
        }
        // Só persiste agendas ainda habilitadas (IDs órfãos/desabilitados somem do picker).
        const enabledIds = new Set(enabledSyncCalendars.map((sc) => sc.id));
        allowlistIdsToSave = allowedCalendarIds.filter((id) => id && enabledIds.has(id));
        if (
          !allowlistIdsToSave.includes(values.google_calendar_sync_calendar_id)
        ) {
          form.setError("google_calendar_sync_calendar_id", {
            type: "manual",
            message: "A agenda padrão deve estar entre as agendas permitidas e habilitadas.",
          });
          toast({
            title: "Allowlist inconsistente",
            description:
              "Remova agendas desabilitadas do allowlist ou reative-as em Integrações antes de salvar.",
            variant: "destructive",
          });
          return;
        }
        if (allowlistIdsToSave.length === 0) {
          toast({
            title: "Selecione as agendas do agente",
            description:
              "Nenhuma agenda habilitada restante no allowlist. Marque ao menos uma agenda ativa.",
            variant: "destructive",
          });
          return;
        }
      } else if (gcalMultiEnabled && !values.google_calendar_enabled) {
        allowlistIdsToSave = [];
      }

      if (catalogEnabled) {
        const isLinkCatalog = catalogStorageProvider === "link";
        if (isLinkCatalog) {
          const link = (catalogFileUrl || "").trim();
          let validLink = false;
          try {
            const u = new URL(link);
            validLink = u.protocol === "http:" || u.protocol === "https:";
          } catch {
            validLink = false;
          }
          if (!validLink) {
            toast({
              title: "Link do catálogo inválido",
              description: "Informe uma URL completa começando com http:// ou https://.",
              variant: "destructive",
            });
            return;
          }
        } else if (!catalogObjectKey && !catalogFileUrl) {
          toast({
            title: "Catálogo incompleto",
            description: "Envie um PDF ou escolha o formato link e informe a URL do site.",
            variant: "destructive",
          });
          return;
        }
      }

      const hoursProblem = validateBusinessHours(businessHours);
      if (hoursProblem) {
        toast({
          title: "Horário de funcionamento inválido",
          description: `${hoursProblem} Ajuste na aba Negócio antes de salvar.`,
          variant: "destructive",
        });
        return;
      }

      let agentId = agent?.id;
      const agentData = {
        name: values.name,
        display_name: values.display_name || null,
        greeting_message: values.greeting_message || null,
        role: values.role,
        tone: values.tone,
        language: values.language,
        timezone: values.timezone,
        is_active: values.is_active,
        restrict_to_business_hours: values.restrict_to_business_hours,
        google_calendar_enabled: values.google_calendar_enabled,
        google_calendar_sync_calendar_id: values.google_calendar_enabled
          ? (values.google_calendar_sync_calendar_id ||
              (!gcalMultiEnabled ? agent?.google_calendar_sync_calendar_id ?? null : null))
          : null,
        appointment_duration: values.appointment_duration,
        max_appointment_options: values.max_appointment_options,
        scheduling_start_offset_days: values.scheduling_start_offset_days,
        scheduling_search_days: values.scheduling_search_days,
        allow_overlapping_appointments: values.allow_overlapping_appointments,
        address_enabled: values.address_enabled,
        phone_numbers: values.phone_numbers.length > 0 ? values.phone_numbers.filter(p => p.trim() !== "") : null,
        phone_numbers_enabled: values.phone_numbers_enabled,
        social_media: values.social_media,
        social_media_enabled: values.social_media_enabled,
        comment_website_link_placement: values.comment_website_link_placement,
        comment_product_link_placement: values.comment_product_link_placement,
        comment_reply_mode: values.comment_reply_mode,
        establishment_name: values.establishment_name || null,
        qualification_questions: qualificationQuestions.filter(q => q.trim() !== "") as unknown as any,
        business_rules_list: businessRulesList.filter(r => r.trim() !== "") as unknown as any,
        agent_instructions: agentInstructions.filter(i => i.trim() !== "") as unknown as any,
        addresses: addresses.filter(a => a.address.trim() !== "") as unknown as any,
        can_schedule: values.can_schedule,
        can_reschedule: values.can_reschedule,
        can_cancel: values.can_cancel,
        cnpj: values.cnpj || null,
        other_social_media: values.other_social_media || null,
        payment_methods: values.payment_methods.length > 0 ? values.payment_methods : null,
        payment_installments: values.payment_installments || null,
        show_prices_to_customer: values.price_disclosure_mode !== "never",
        price_disclosure_mode: values.price_disclosure_mode,
        price_disclosure_condition: (values.price_disclosure_condition || "").trim() || null,
        delivery_info: values.delivery_info || null,
        delivery_enabled: values.delivery_enabled,
        about_company: values.about_company || null,
        target_audience: values.target_audience || null,
        value_proposition: values.value_proposition || null,
        agent_goal: values.agent_goal || null,
        desired_next_step: values.desired_next_step || null,
        service_area: values.service_area || null,
        limitations: values.limitations || null,
        business_scope: values.business_scope || null,
        out_of_scope_behavior: values.out_of_scope_behavior || null,
        policies_summary: values.policies_summary || null,
        required_customer_data: values.required_customer_data || null,
        faq_items: faqItems.filter(f => f.question.trim() !== "" && f.answer.trim() !== "") as unknown as any,
        kb_overrides_faq: kbOverridesFaq,
        business_category: businessCategory,
        business_tags: businessTags.filter(t => t.trim() !== ""),
        human_handoff_enabled: humanHandoffEnabled,
        human_handoff_rules: humanHandoffRulesV2.length > 0 
          ? humanHandoffRulesV2.map(r => r.condition).filter(c => c.trim() !== "") as unknown as any
          : humanHandoffRules.filter(r => r.trim() !== "") as unknown as any,
        human_handoff_rules_v2: humanHandoffRulesV2.filter(r => r.condition.trim() !== "") as unknown as any,
        agent_escalation_rules_v2: agentEscalationRulesV2.filter(
          (r) =>
            r.condition.trim() !== "" &&
            r.target_agent_id &&
            r.target_agent_id !== agent?.id,
        ) as unknown as any,
        catalog_file_enabled: catalogEnabled,
        catalog_file_url: catalogEnabled ? ((catalogFileUrl || "").trim() || null) : null,
        catalog_storage_provider: catalogEnabled
          ? (catalogStorageProvider === "link" ? "link" : catalogStorageProvider)
          : null,
        catalog_bucket: catalogEnabled && catalogStorageProvider !== "link" ? catalogBucket : null,
        catalog_object_key: catalogEnabled && catalogStorageProvider !== "link" ? catalogObjectKey : null,
        catalog_file_name: catalogEnabled
          ? (catalogStorageProvider === "link" ? "Catálogo (link)" : catalogFileName)
          : null,
        catalog_mime_type: catalogEnabled && catalogStorageProvider !== "link" ? catalogMimeType : null,
        catalog_file_size_bytes: catalogEnabled && catalogStorageProvider !== "link" ? catalogFileSize : null,
        catalog_public_url: catalogEnabled && catalogStorageProvider !== "link" ? catalogPublicUrl : null,
        catalog_trigger_phrases: catalogEnabled
          ? normalizeCatalogTriggerPhrases(catalogTriggerPhrases)
          : [],
        llm_temperature: values.llm_temperature,
      };
      if (agent) {
        const {
          error
        } = await supabase.from("agents").update(agentData as any).eq("id", agent.id);
        if (error) throw error;
      } else {
        const {
          data: newAgent,
          error
        } = await supabase.from("agents").insert({
          workspace_id: workspaceId,
          ...agentData
        } as any).select().single();
        if (error) throw error;
        agentId = newAgent.id;
      }
      if (agentId) {
        await supabase.from("agent_products").delete().eq("agent_id", agentId);
        if (selectedProductIds.length > 0) {
          const agentProducts = selectedProductIds.map((productId) => ({
            agent_id: agentId,
            product_id: productId,
          }));
          await insertInBatches(supabase.from("agent_products"), agentProducts);
        }

        if (allowlistIdsToSave !== null) {
          // Seguro contra wipe: upsert primeiro, só depois remove o que sobrou.
          // (delete-all → insert falhava e deixava o agente sem allowlist em produção.)
          if (allowlistIdsToSave.length > 0) {
            const { error: allowUpsertErr } = await supabase.from("agent_google_calendars").upsert(
              allowlistIdsToSave.map((google_calendar_sync_calendar_id) => ({
                agent_id: agentId,
                google_calendar_sync_calendar_id,
              })),
              { onConflict: "agent_id,google_calendar_sync_calendar_id", ignoreDuplicates: true },
            );
            if (allowUpsertErr) {
              const msg = String(allowUpsertErr.message || "");
              if (msg.includes("agent_google_calendars") || allowUpsertErr.code === "42P01") {
                throw new Error(
                  "Tabela agent_google_calendars ausente. Aplique a migration 20260709010000_agent_google_calendars antes de salvar o allowlist.",
                );
              }
              throw allowUpsertErr;
            }
          }
          const { data: existingAllow, error: existingErr } = await supabase
            .from("agent_google_calendars")
            .select("google_calendar_sync_calendar_id")
            .eq("agent_id", agentId);
          if (existingErr) {
            const msg = String(existingErr.message || "");
            if (msg.includes("agent_google_calendars") || existingErr.code === "42P01") {
              throw new Error(
                "Tabela agent_google_calendars ausente. Aplique a migration 20260709010000_agent_google_calendars antes de salvar o allowlist.",
              );
            }
            throw existingErr;
          }
          const keep = new Set(allowlistIdsToSave);
          const toRemove = (existingAllow ?? [])
            .map((r) => r.google_calendar_sync_calendar_id)
            .filter((id): id is string => !!id && !keep.has(id));
          if (toRemove.length > 0) {
            const { error: allowPruneErr } = await supabase
              .from("agent_google_calendars")
              .delete()
              .eq("agent_id", agentId)
              .in("google_calendar_sync_calendar_id", toRemove);
            if (allowPruneErr) throw allowPruneErr;
          }
        }

        // Seguro contra wipe: grava os dias ativos primeiro e só depois remove os demais.
        // (delete-all → insert falhando deixava o agente sem horário.)
        const hoursToSave = businessHours.some((h) => h.is_enabled)
          ? businessHours.filter((h) => h.is_enabled).map((h) => ({
              agent_id: agentId,
              day_of_week: h.day_of_week,
              is_enabled: h.is_enabled,
              start_time: h.start_time,
              end_time: h.end_time,
              break_start: h.break_start || null,
              break_end: h.break_end || null,
            }))
          : [];
        if (hoursToSave.length > 0) {
          const { error: hoursError } = await supabase
            .from("agent_business_hours")
            .upsert(hoursToSave, { onConflict: "agent_id,day_of_week" });
          if (hoursError) {
            console.error("agent_business_hours upsert failed", hoursError);
            throw new Error(
              "Não foi possível salvar o horário de funcionamento. Confira os horários e intervalos de cada dia.",
            );
          }
        }
        const keepDays = hoursToSave.map((h) => h.day_of_week);
        let pruneHours = supabase.from("agent_business_hours").delete().eq("agent_id", agentId);
        if (keepDays.length > 0) pruneHours = pruneHours.not("day_of_week", "in", `(${keepDays.join(",")})`);
        const { error: pruneHoursError } = await pruneHours;
        if (pruneHoursError) console.error("agent_business_hours prune failed", pruneHoursError);

        const { data: existingConfig } = await supabase
          .from("agent_followup_config")
          .select("id")
          .eq("agent_id", agentId)
          .maybeSingle();
        const followUpPayload = {
          enabled: followUpEnabled,
          interval_minutes: followUpInterval,
          max_attempts: followUpMaxAttempts,
        };
        const { error: followUpError } = existingConfig
          ? await supabase.from("agent_followup_config").update(followUpPayload).eq("id", existingConfig.id)
          : await supabase.from("agent_followup_config").insert({ agent_id: agentId, ...followUpPayload });
        if (followUpError) {
          console.error("agent_followup_config save failed", followUpError);
          throw new Error("O agente foi salvo, mas não foi possível salvar o follow-up. Tente novamente.");
        }
      }
      toast({
        title: agent ? "Agente atualizado" : "Agente criado",
        description: "Alterações salvas. Você pode continuar editando ou fechar quando quiser.",
      });
      if (agentId) onSaved?.(agentId);
      setTimeout(() => {
        baselineRef.current = JSON.stringify(dirtyStateRef.current);
      }, 0);
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  dirtyStateRef.current = {
    form: form.getValues(),
    qualificationQuestions,
    businessRulesList,
    agentInstructions,
    addresses,
    faqItems,
    kbOverridesFaq,
    businessCategory,
    businessTags,
    humanHandoffEnabled,
    humanHandoffRules,
    humanHandoffRulesV2,
    agentEscalationRulesV2,
    catalogEnabled,
    catalogFileUrl,
    catalogObjectKey,
    catalogStorageProvider,
    catalogFileName,
    catalogFileSize,
    catalogPublicUrl,
    catalogTriggerPhrases: [...catalogTriggerPhrases].sort(),
    selectedProductIds: [...selectedProductIds].sort(),
    allowedCalendarIds: [...allowedCalendarIds].sort(),
    businessHours: [...businessHours].sort((a, b) => a.day_of_week - b.day_of_week),
    followUpEnabled,
    followUpInterval,
    followUpMaxAttempts,
  };

  const hasUnsavedChanges = () =>
    baselineRef.current !== null && JSON.stringify(dirtyStateRef.current) !== baselineRef.current;

  const requestClose = () => {
    if (hasUnsavedChanges()) {
      setUnsavedCloseOpen(true);
      return;
    }
    onOpenChange(false);
  };

  const handleMainDialogOpenChange = (next: boolean) => {
    if (next) {
      onOpenChange(true);
      return;
    }
    requestClose();
  };

  const confirmDiscardAndClose = () => {
    setUnsavedCloseOpen(false);
    onOpenChange(false);
  };

  const buildConfigSnapshot = (): AgentConfigSnapshot => ({
    form: form.getValues() as AgentConfigSnapshot["form"],
    qualificationQuestions,
    businessRulesList,
    agentInstructions,
    addresses,
    faqItems,
    businessCategory,
    businessTags,
    humanHandoffEnabled,
    humanHandoffRules,
    humanHandoffRulesV2,
    agentEscalationRulesV2,
    catalogEnabled,
    catalogFileUrl,
    catalogStorageProvider,
    catalogBucket,
    catalogObjectKey,
    catalogMimeType,
    catalogFileSize,
    catalogPublicUrl,
    catalogFileName,
    catalogTriggerPhrases,
    selectedProductIds,
    businessHours,
    followUpEnabled,
    followUpInterval,
    followUpMaxAttempts,
  });

  const applyImportedSnapshot = (snapshot: AgentConfigSnapshot) => {
    form.reset(snapshot.form);
    setQualificationQuestions(snapshot.qualificationQuestions);
    setBusinessRulesList(snapshot.businessRulesList);
    setAgentInstructions(snapshot.agentInstructions);
    setAddresses(snapshot.addresses);
    setFaqItems(snapshot.faqItems);
    setBusinessCategory(snapshot.businessCategory);
    setBusinessTags(snapshot.businessTags);
    setHumanHandoffEnabled(snapshot.humanHandoffEnabled);
    setHumanHandoffRules(snapshot.humanHandoffRules);
    setHumanHandoffRulesV2(snapshot.humanHandoffRulesV2);
    setAgentEscalationRulesV2(snapshot.agentEscalationRulesV2 ?? []);
    setCatalogEnabled(snapshot.catalogEnabled);
    setCatalogFileUrl(snapshot.catalogFileUrl);
    setCatalogStorageProvider(snapshot.catalogStorageProvider);
    setCatalogBucket(snapshot.catalogBucket);
    setCatalogObjectKey(snapshot.catalogObjectKey);
    setCatalogMimeType(snapshot.catalogMimeType);
    setCatalogFileSize(snapshot.catalogFileSize);
    setCatalogPublicUrl(snapshot.catalogPublicUrl);
    setCatalogFileName(snapshot.catalogFileName);
    setCatalogTriggerPhrases(snapshot.catalogTriggerPhrases ?? []);
    setSelectedProductIds(snapshot.selectedProductIds);
    setBusinessHours(snapshot.businessHours);
    setFollowUpEnabled(snapshot.followUpEnabled);
    setFollowUpInterval(snapshot.followUpInterval);
    setFollowUpMaxAttempts(snapshot.followUpMaxAttempts);
    toast({
      title: "Configurações importadas",
      description: "Revise os campos e clique em Salvar para persistir no agente.",
    });
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleCopyConfig = async () => {
    const rows = snapshotToRows(buildConfigSnapshot());
    const text = formatRowsAsPlainText(rows, form.getValues().name);
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copiado",
        description: "Markdown por aba (Perfil, Negócio, Comportamento), alinhado ao prompt.",
      });
    } catch {
      toast({
        title: "Não foi possível copiar",
        description: "Permita acesso à área de transferência ou exporte em CSV/XLSX.",
        variant: "destructive",
      });
    }
  };

  const handleExportConfig = (format: "csv" | "xlsx") => {
    const rows = snapshotToRows(buildConfigSnapshot());
    const safeName = (form.getValues().name || "agente").replace(/[^\w\-]+/g, "_").slice(0, 40);
    if (format === "csv") {
      downloadBlob(exportRowsToCsv(rows), `config-${safeName}.csv`);
    } else {
      downloadBlob(exportRowsToXlsx(rows), `config-${safeName}.xlsx`);
    }
    toast({
      title: "Exportado",
      description: format === "csv" ? "Arquivo CSV baixado." : "Arquivo Excel baixado.",
    });
  };

  const handleDownloadTemplate = (format: "csv" | "xlsx") => {
    if (format === "csv") {
      downloadBlob(generateAgentConfigTemplateCsv(), "template-config-agente.csv");
    } else {
      downloadBlob(generateAgentConfigTemplateXlsx(), "template-config-agente.xlsx");
    }
      toast({
        title: "Template baixado",
        description:
          "Preencha a coluna Sua resposta e importe de volta. Listas: uma linha por item.",
      });
  };

  return <>
      <Dialog open={open} onOpenChange={handleMainDialogOpenChange}>
        <DialogContent className="w-full max-w-[100vw] h-[100dvh] max-h-[100dvh] sm:h-auto sm:max-h-[95vh] sm:w-[95vw] sm:max-w-4xl flex flex-col p-0 gap-0 agent-dialog-bg rounded-none sm:rounded-lg fixed inset-0 left-0 top-0 right-0 bottom-0 translate-x-0 translate-y-0 sm:inset-auto sm:left-[50%] sm:top-[50%] sm:right-auto sm:bottom-auto sm:translate-x-[-50%] sm:translate-y-[-50%] overflow-hidden">
          <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 pr-12 border-b flex-shrink-0 bg-background">
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-agent-primary shrink-0" />
              {agent ? "Configurar Agente" : "Novo Agente"}
            </DialogTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Configure com calma — cada detalhe importa.
            </p>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
              <Tabs defaultValue="perfil" className="flex-1 flex flex-col min-h-0">
                <div className="px-4 sm:px-6 pt-4 flex-shrink-0 bg-background pb-4">
                  <TabsList className="w-full flex bg-muted/50 rounded-lg p-1 h-auto gap-0">
                    <TabsTrigger value="perfil" className="flex-1 min-w-0 mx-0 px-2 sm:px-3 flex items-center justify-center gap-1.5 py-2.5 text-xs sm:text-sm rounded-md">
                      <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="truncate">Perfil</span>
                    </TabsTrigger>
                    <TabsTrigger value="negocio" className="flex-1 min-w-0 mx-0 px-2 sm:px-3 flex items-center justify-center gap-1.5 py-2.5 text-xs sm:text-sm rounded-md">
                      <Building2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="truncate">Negócio</span>
                    </TabsTrigger>
                    <TabsTrigger value="comportamento" className="flex-1 min-w-0 mx-0 px-2 sm:px-3 flex items-center justify-center gap-1.5 py-2.5 text-xs sm:text-sm rounded-md">
                      <Settings className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="truncate">Comportamento</span>
                    </TabsTrigger>
                  </TabsList>
                </div>

                <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-6 py-4 sm:py-6">
                  {/* TAB: PERFIL */}
                  <TabsContent value="perfil" className="mt-0 space-y-4">
                    <Accordion type="multiple" className="space-y-3">
                      <AgentConfigSection
                        value="identificacao"
                        emoji="👤"
                        title="Identificação"
                        infoKey="identificacao"
                        hint={form.watch("display_name") || form.watch("name") || "Configure nome e saudação"}
                      >
                        <FormField control={form.control} name="name" render={({
                        field
                      }) => <FormItem>
                              <FormLabel className="text-sm">Nome Interno</FormLabel>
                              <FormControl>
                                <Input placeholder="Ex: Agente de Vendas" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>} />

                        <FormField control={form.control} name="display_name" render={({
                        field
                      }) => <FormItem>
                              <FormLabel className="text-sm">Nome de Apresentação</FormLabel>
                              <FormControl>
                                <Input placeholder="Ex: Maria, João" {...field} />
                              </FormControl>
                              <p className="agent-hint">Nome que o agente usa nas conversas</p>
                              <FormMessage />
                            </FormItem>} />

                        <FormField control={form.control} name="greeting_message" render={({
                        field
                      }) => <FormItem>
                              <FormLabel className="text-sm">Mensagem de Saudação</FormLabel>
                              <FormControl>
                                <Textarea placeholder="Ex: Olá! Sou a Maria, como posso ajudar você hoje? 😊" className="min-h-[80px] resize-none" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>} />
                      </AgentConfigSection>

                      <AgentConfigSection
                        value="personalidade"
                        emoji="🎨"
                        title="Personalidade"
                        infoKey="personalidade"
                        hint={[
                          agentRoleLabels[form.watch("role")] ?? "Função",
                          toneOptions.find((tone) => tone.value === form.watch("tone"))?.label ?? "Tom",
                        ].join(" · ")}
                      >
                        <FormField control={form.control} name="role" render={({
                        field
                      }) => <FormItem>
                              <FormLabel className="text-sm">Função</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selecione a função" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="vendas">💼 Vendas</SelectItem>
                                  <SelectItem value="suporte">🛟 Suporte</SelectItem>
                                  <SelectItem value="sdr">📞 SDR</SelectItem>
                                  <SelectItem value="agendamentos">📅 Agendamentos</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>} />

                        <FormField control={form.control} name="tone" render={({
                        field
                      }) => <FormItem>
                              <FormLabel className="text-sm">Tom de Voz</FormLabel>
                              <div className="flex flex-wrap gap-2 pt-1">
                                {toneOptions.map(tone => <button key={tone.value} type="button" onClick={() => field.onChange(tone.value)} className={cn("px-3 py-1.5 rounded-full border text-sm transition-all", field.value === tone.value ? "bg-agent-primary text-white border-agent-primary" : "bg-muted/50 hover:bg-muted border-border hover:border-agent-primary/50")}>
                                    {tone.icon} {tone.label}
                                  </button>)}
                              </div>
                              <FormMessage />
                            </FormItem>} />

                        <FormField control={form.control} name="language" render={({
                        field
                      }) => <FormItem>
                              <FormLabel className="text-sm">Idioma</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selecione o idioma" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="pt-BR">🇧🇷 Português (BR)</SelectItem>
                                  <SelectItem value="en-US">🇺🇸 Inglês (US)</SelectItem>
                                  <SelectItem value="es-ES">🇪🇸 Espanhol</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>} />
                      </AgentConfigSection>

                      <AgentConfigSection
                        value="perguntas-qualificacao"
                        emoji="❓"
                        title="Perguntas de Qualificação"
                        infoKey="perguntas_qualificacao"
                        hint={`${qualificationQuestions.filter((q) => q.trim()).length} pergunta(s)`}
                      >
                      <p className="text-sm text-muted-foreground">
                        Perguntas que o agente DEVE fazer antes de prosseguir com o atendimento
                      </p>

                      <DynamicListInput
                        items={qualificationQuestions}
                        onChange={setQualificationQuestions}
                        placeholder="Digite uma pergunta..."
                        addLabel="Adicionar Pergunta"
                        emptyMessage="Nenhuma pergunta configurada"
                        sortable
                      />

                      {qualificationQuestions.length > 0 && <Alert className="bg-agent-primary/5 border-agent-primary/20">
                          <Info className="h-4 w-4 text-agent-primary" />
                          <AlertDescription className="text-xs">
                            O agente fará uma pergunta por vez, na ordem da lista, e aguardará a resposta antes de continuar. Arraste pelo ícone à esquerda para alterar a ordem no prompt.
                          </AlertDescription>
                        </Alert>}
                      </AgentConfigSection>

                      <AgentConfigSection
                        value="instrucoes"
                        emoji="💡"
                        title="Instruções do Agente"
                        infoKey="instrucoes_agente"
                        hint={`${agentInstructions.filter((i) => i.trim()).length} instrução(ões)`}
                      >
                      <p className="text-sm text-muted-foreground">
                        Instruções específicas que o agente deve seguir rigorosamente
                      </p>

                      <DynamicListInput
                        sortable
                        items={agentInstructions}
                        onChange={setAgentInstructions}
                        placeholder="Ex: Sempre ofereça desconto de 10% para primeira compra"
                        addLabel="Adicionar Instrução"
                        emptyMessage="Nenhuma instrução configurada"
                      />

                      {agentInstructions.length > 0 && <Alert className="bg-agent-accent/10 border-agent-accent/30">
                          <Info className="h-4 w-4 text-agent-accent" />
                          <AlertDescription className="text-xs">
                            Estas instruções têm prioridade máxima e serão seguidas rigorosamente.
                          </AlertDescription>
                        </Alert>}
                      </AgentConfigSection>
                    </Accordion>
                  </TabsContent>

                  {/* TAB: NEGÓCIO */}
                  <TabsContent value="negocio" className="mt-0 space-y-4">
                    <Accordion type="multiple" className="space-y-3">
                      <AgentConfigSection
                        value="sobre-empresa"
                        emoji="📝"
                        title="Sobre a Empresa"
                        infoKey="sobre_empresa"
                        hint={
                          form.watch("about_company")?.trim()
                            ? "Biografia preenchida"
                            : "Biografia, missão e valores"
                        }
                      >
                      <p className="text-sm text-muted-foreground mb-2">
                        Biografia, missão, visão e valores da empresa
                      </p>
                      
                      <FormField 
                        control={form.control} 
                        name="about_company" 
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Textarea
                                {...field}
                                placeholder="Descreva sua empresa: história, missão, visão, valores, diferenciais..."
                                className="min-h-[120px] resize-y"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )} 
                      />
                      </AgentConfigSection>

                      <AgentConfigSection
                        value="area-atuacao"
                        emoji="📍"
                        title="Local de Atuação da Empresa"
                        infoKey="area_atuacao"
                        hint={
                          form.watch("business_scope")?.trim()
                            ? "Área geográfica definida"
                            : "Cidades e regiões atendidas"
                        }
                      >
                      <p className="text-sm text-muted-foreground mb-3">
                        Informe as cidades, bairros ou regiões onde a empresa atende fisicamente. O agente usará isso para identificar clientes fora da cobertura geográfica.
                      </p>

                      <FormField
                        control={form.control}
                        name="business_scope"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm">Local de atuação</FormLabel>
                            <FormControl>
                              <Textarea
                                {...field}
                                placeholder="Ex: Atendemos presencialmente em São Paulo (capital), Guarulhos, Osasco e região metropolitana. Não realizamos visitas fora do estado de SP."
                                className="min-h-[100px] resize-y"
                                maxLength={800}
                              />
                            </FormControl>
                            <p className="agent-hint">Liste cidades, regiões ou limites geográficos — não segmento de mercado, produtos ou serviços.</p>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="mt-4">
                        <FormField
                          control={form.control}
                          name="out_of_scope_behavior"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm">O que fazer quando o cliente estiver fora da área geográfica</FormLabel>
                              <FormControl>
                                <Textarea
                                  {...field}
                                  placeholder="Ex: Agradeça o contato, informe gentilmente que a empresa atende apenas na região de São Paulo e, se possível, indique uma alternativa ou encaminhe para um atendente humano."
                                  className="min-h-[100px] resize-y"
                                  maxLength={800}
                                />
                              </FormControl>
                              <p className="agent-hint">Instrução vinculante: o agente seguirá exatamente esta orientação ao identificar cliente fora da região atendida.</p>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      </AgentConfigSection>

                      <AgentConfigSection
                        value="contexto-estrategico"
                        emoji="🎯"
                        title="Contexto Estratégico"
                        infoKey="contexto_estrategico"
                        hint="Público, diferenciais e objetivos"
                      >
                      <p className="text-sm text-muted-foreground mb-3">
                        Campos curtos para orientar o agente sem aumentar demais o prompt
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField control={form.control} name="target_audience" render={({
                        field
                      }) => <FormItem>
                              <FormLabel className="text-sm">Público-alvo</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder="Ex: Mulheres de 30 a 55 anos buscando estética facial premium"
                                  className="min-h-[80px] resize-none"
                                  maxLength={600}
                                  {...field}
                                />
                              </FormControl>
                              <p className="agent-hint">Perfil de cliente ideal e principais dores</p>
                              <FormMessage />
                            </FormItem>} />

                        <FormField control={form.control} name="value_proposition" render={({
                        field
                      }) => <FormItem>
                              <FormLabel className="text-sm">Diferenciais</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder="Ex: Atendimento rápido, especialistas certificados e produtos próprios"
                                  className="min-h-[80px] resize-none"
                                  maxLength={600}
                                  {...field}
                                />
                              </FormControl>
                              <p className="agent-hint">Proposta de valor e motivos para escolher a empresa</p>
                              <FormMessage />
                            </FormItem>} />

                        <FormField control={form.control} name="agent_goal" render={({
                        field
                      }) => <FormItem>
                              <FormLabel className="text-sm">Objetivo do Agente</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder="Ex: Qualificar o cliente e agendar uma avaliação"
                                  className="min-h-[80px] resize-none"
                                  maxLength={400}
                                  {...field}
                                />
                              </FormControl>
                              <p className="agent-hint">Resultado principal que o agente deve buscar</p>
                              <FormMessage />
                            </FormItem>} />

                        <FormField control={form.control} name="desired_next_step" render={({
                        field
                      }) => <FormItem>
                              <FormLabel className="text-sm">Próximo Passo Desejado</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder="Ex: Conduzir para agendamento ou envio de orçamento"
                                  className="min-h-[80px] resize-none"
                                  maxLength={400}
                                  {...field}
                                />
                              </FormControl>
                              <p className="agent-hint">CTA preferencial após entender a necessidade</p>
                              <FormMessage />
                            </FormItem>} />

                        <FormField control={form.control} name="service_area" render={({
                        field
                      }) => <FormItem>
                              <FormLabel className="text-sm">Área de Atendimento</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder="Ex: Atende Curitiba e região metropolitana"
                                  className="min-h-[80px] resize-none"
                                  maxLength={500}
                                  {...field}
                                />
                              </FormControl>
                              <p className="agent-hint">Cobertura, regiões atendidas e limites geográficos</p>
                              <FormMessage />
                            </FormItem>} />

                        <FormField control={form.control} name="limitations" render={({
                        field
                      }) => <FormItem>
                              <FormLabel className="text-sm">Limites e Restrições</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder="Ex: Não faz diagnóstico, não promete resultado e não atende fora da cidade"
                                  className="min-h-[80px] resize-none"
                                  maxLength={600}
                                  {...field}
                                />
                              </FormControl>
                              <p className="agent-hint">O que a empresa não faz ou não deve prometer</p>
                              <FormMessage />
                            </FormItem>} />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <FormField control={form.control} name="policies_summary" render={({
                        field
                      }) => <FormItem>
                              <FormLabel className="text-sm">Políticas Importantes</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder="Ex: Cancelamentos com 24h de antecedência. Trocas em até 7 dias com etiqueta."
                                  className="min-h-[100px] resize-none"
                                  maxLength={1000}
                                  {...field}
                                />
                              </FormControl>
                              <p className="agent-hint">Resumo curto de troca, cancelamento, garantia ou reembolso</p>
                              <FormMessage />
                            </FormItem>} />

                        <FormField control={form.control} name="required_customer_data" render={({
                        field
                      }) => <FormItem>
                              <FormLabel className="text-sm">Dados a Coletar</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder="Ex: Nome, cidade, serviço desejado e melhor horário"
                                  className="min-h-[100px] resize-none"
                                  maxLength={600}
                                  {...field}
                                />
                              </FormControl>
                              <p className="agent-hint">Dados úteis para qualificar sem parecer formulário</p>
                              <FormMessage />
                            </FormItem>} />
                      </div>
                      </AgentConfigSection>

                      <AgentConfigSection
                        value="faq"
                        emoji="❓"
                        title="Perguntas Frequentes (FAQ)"
                        infoKey="faq"
                        hint={`${faqItems.filter((item) => item.question.trim() || item.answer.trim()).length} item(ns)`}
                      >
                      <p className="text-sm text-muted-foreground mb-3">
                        Perguntas e respostas que a IA deve saber responder diretamente
                      </p>

                      <div className="space-y-4">
                        {faqItems.map((item, index) => (
                          <div key={index} className="rounded-lg border border-border p-3 space-y-2 bg-muted/30">
                            <div className="flex items-start gap-2">
                              <div className="flex-1 space-y-2">
                                <Input
                                  value={item.question}
                                  onChange={(e) => {
                                    const newFaq = [...faqItems];
                                    newFaq[index] = { ...newFaq[index], question: e.target.value };
                                    setFaqItems(newFaq);
                                  }}
                                  placeholder="Pergunta: Ex: Qual o prazo de entrega?"
                                  className="text-sm"
                                />
                                <Textarea
                                  value={item.answer}
                                  maxLength={1500}
                                  onChange={(e) => {
                                    const newFaq = [...faqItems];
                                    newFaq[index] = { ...newFaq[index], answer: e.target.value };
                                    setFaqItems(newFaq);
                                  }}
                                  placeholder="Resposta: Ex: O prazo de entrega é de 3 a 5 dias úteis."
                                  className="min-h-[60px] resize-none text-sm"
                                />
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="flex-shrink-0 mt-1"
                                onClick={() => setFaqItems(faqItems.filter((_, i) => i !== index))}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setFaqItems([...faqItems, { question: "", answer: "" }])}
                          className="w-full border-dashed"
                        >
                          <Plus className="w-4 h-4 mr-2" /> Adicionar Pergunta
                        </Button>
                      </div>
                      </AgentConfigSection>

                      <AgentConfigSection
                        value="base-conhecimento"
                        emoji="📚"
                        title="Base de conhecimento"
                        infoKey="base_conhecimento"
                        hint={
                          agent?.id
                            ? `${kbDocCount}/10 documentos`
                            : "Salve o agente para enviar arquivos"
                        }
                      >
                        <KnowledgeBaseSection
                          workspaceId={workspaceId}
                          agentId={agent?.id}
                          kbOverridesFaq={kbOverridesFaq}
                          onKbOverridesFaqChange={setKbOverridesFaq}
                          onDocCountChange={setKbDocCount}
                        />
                      </AgentConfigSection>

                      <AgentConfigSection
                        value="setor-negocio"
                        emoji="🏷️"
                        title="Setor do Negócio"
                        infoKey="setor_negocio"
                        hint={`${businessCategory !== "other" ? "Setor configurado" : "Genérico"} · ${businessTags.length} tag(s)`}
                      >
                      <p className="text-xs text-muted-foreground mb-3">
                        Selecione o setor para ativar regras especializadas no comportamento do agente.
                      </p>

                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Categoria</label>
                          <Select value={businessCategory} onValueChange={setBusinessCategory}>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o setor" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="other">Genérico / Outro</SelectItem>
                              <SelectItem value="healthcare">Saúde</SelectItem>
                              <SelectItem value="aesthetics">Estética</SelectItem>
                              <SelectItem value="dental">Odontologia</SelectItem>
                              <SelectItem value="orthopedic">Ortopedia</SelectItem>
                              <SelectItem value="finance">Finanças</SelectItem>
                              <SelectItem value="real_estate">Imobiliário</SelectItem>
                              <SelectItem value="retail_technical">Varejo Técnico</SelectItem>
                              <SelectItem value="hardware">Ferragens / Hardware</SelectItem>
                              <SelectItem value="retail_general">Varejo Geral</SelectItem>
                              <SelectItem value="sports_store">Loja Esportiva</SelectItem>
                              <SelectItem value="semi_jewelry">Semi-joias</SelectItem>
                              <SelectItem value="services">Serviços</SelectItem>
                              <SelectItem value="scheduling">Agendamentos</SelectItem>
                              <SelectItem value="pool_reseller">Revenda de Piscinas</SelectItem>
                              <SelectItem value="ecommerce">E-commerce / Loja online</SelectItem>
                              <SelectItem value="saas_platform">SaaS / Plataforma com planos e assinaturas</SelectItem>
                              <SelectItem value="fashion_retail">Loja de roupas / moda</SelectItem>
                              <SelectItem value="plastic_surgery">Clínica de cirurgia plástica</SelectItem>
                              <SelectItem value="premium_home_textiles">Cortinas e têxteis de alto padrão</SelectItem>
                              <SelectItem value="education">Escola / curso / treinamento</SelectItem>
                              <SelectItem value="restaurant">Restaurante / bar / delivery</SelectItem>
                              <SelectItem value="beauty_salon">Salão de beleza / barbearia</SelectItem>
                              <SelectItem value="automotive_service">Oficina / auto peças / serviços automotivos</SelectItem>
                              <SelectItem value="trade_union">Sindicatos / entidades de classe</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-medium">Tags do Negócio</label>
                          {businessCategory === "trade_union" ? (
                            <p className="text-xs text-muted-foreground">
                              Para <strong>sindicatos</strong>, adicione até <strong>3</strong> tags de segmento (ordem alfabética no prompt). Exemplos:{" "}
                              <code className="text-[0.7rem]">lojistas</code>, <code className="text-[0.7rem]">comercio</code>,{" "}
                              <code className="text-[0.7rem]">varejistas</code>, <code className="text-[0.7rem]">varejo</code>,{" "}
                              <code className="text-[0.7rem]">farmaceuticos</code>, <code className="text-[0.7rem]">farmacia</code>,{" "}
                              <code className="text-[0.7rem]">industria</code>, <code className="text-[0.7rem]">transporte</code>,{" "}
                              <code className="text-[0.7rem]">servicos</code>, <code className="text-[0.7rem]">alimentacao</code>,{" "}
                              <code className="text-[0.7rem]">hotelaria</code>, <code className="text-[0.7rem]">bancarios</code>,{" "}
                              <code className="text-[0.7rem]">rural</code>, <code className="text-[0.7rem]">agro</code>,{" "}
                              <code className="text-[0.7rem]">professores</code>, <code className="text-[0.7rem]">educacao</code>.
                            </p>
                          ) : (
                            <p className="text-xs text-muted-foreground">
                              Tags opcionais para contextualizar ainda mais o agente (ex: "delivery", "b2b", "atacado").
                            </p>
                          )}
                          <DynamicListInput
                            items={businessTags}
                            onChange={setBusinessTags}
                            placeholder={
                              businessCategory === "trade_union"
                                ? "Ex: varejistas, farmaceuticos, transporte…"
                                : "Ex: delivery, b2b, premium..."
                            }
                            addLabel="Adicionar Tag"
                            emptyMessage="Nenhuma tag adicionada"
                            maxItems={10}
                            showNumbers={false}
                          />
                        </div>
                      </div>
                      </AgentConfigSection>

                      <AgentConfigSection
                        value="dados-negocio"
                        emoji="🏢"
                        title="Dados do Negócio"
                        infoKey="dados_negocio"
                        hint={form.watch("establishment_name")?.trim() || "Nome, CNPJ e telefones"}
                      >
                        <FormField control={form.control} name="establishment_name" render={({
                        field
                      }) => <FormItem>
                              <FormLabel className="text-sm">Nome do Estabelecimento</FormLabel>
                              <FormControl>
                                <Input placeholder="Ex: Salão da Maria" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>} />

                        <FormField control={form.control} name="cnpj" render={({
                        field
                      }) => <FormItem>
                              <FormLabel className="text-sm">CNPJ</FormLabel>
                              <FormControl>
                                <Input placeholder="00.000.000/0000-00" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>} />

                        <FormField control={form.control} name="phone_numbers" render={({
                        field
                      }) => <FormItem>
                              <div className="flex items-center justify-between">
                                <FormLabel className="text-sm">Telefones</FormLabel>
                                <FormField control={form.control} name="phone_numbers_enabled" render={({
                            field: switchField
                          }) => <div className="flex items-center gap-2">
                                      <span className="text-xs text-muted-foreground">Incluir</span>
                                      <Switch checked={switchField.value} onCheckedChange={switchField.onChange} />
                                    </div>} />
                              </div>
                              <FormControl>
                                <div className="space-y-2">
                                  {field.value.map((phone, index) => <div key={index} className="flex gap-2">
                                      <Input value={phone} onChange={e => {
                                const newPhones = [...field.value];
                                newPhones[index] = e.target.value;
                                field.onChange(newPhones);
                              }} placeholder="(00) 00000-0000" />
                                      <Button type="button" variant="ghost" size="icon" onClick={() => {
                                const newPhones = field.value.filter((_, i) => i !== index);
                                field.onChange(newPhones);
                              }}>
                                        <X className="w-4 h-4" />
                                      </Button>
                                    </div>)}
                                  <Button type="button" variant="outline" size="sm" onClick={() => field.onChange([...field.value, ""])} className="w-full border-dashed">
                                    <Plus className="w-4 h-4 mr-2" /> Adicionar Telefone
                                  </Button>
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>} />
                      </AgentConfigSection>

                      <AgentConfigSection
                        value="enderecos"
                        emoji="🏠"
                        title="Endereços"
                        infoKey="enderecos"
                        hint={form.watch("address_enabled") ? "Incluído no prompt" : "Desativado"}
                        headerAction={
                          <FormField
                            control={form.control}
                            name="address_enabled"
                            render={({ field }) => (
                              <Switch checked={field.value} onCheckedChange={field.onChange} />
                            )}
                          />
                        }
                      >
                        <AddressManager addresses={addresses} onChange={setAddresses} />
                      </AgentConfigSection>

                      <AgentConfigSection
                        value="redes-sociais"
                        emoji="🌐"
                        title="Redes Sociais"
                        infoKey="redes_sociais"
                        hint={form.watch("social_media_enabled") ? "Incluído no prompt" : "Desativado"}
                        headerAction={
                          <FormField
                            control={form.control}
                            name="social_media_enabled"
                            render={({ field }) => (
                              <Switch checked={field.value} onCheckedChange={field.onChange} />
                            )}
                          />
                        }
                      >
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                        <FormField control={form.control} name="social_media.instagram" render={({
                        field
                      }) => <FormItem>
                              <FormLabel className="text-sm">📷 Instagram</FormLabel>
                              <FormControl>
                                <Input placeholder="@usuario" {...field} />
                              </FormControl>
                            </FormItem>} />
                        <FormField control={form.control} name="social_media.facebook" render={({
                        field
                      }) => <FormItem>
                              <FormLabel className="text-sm">👥 Facebook</FormLabel>
                              <FormControl>
                                <Input placeholder="facebook.com/..." {...field} />
                              </FormControl>
                            </FormItem>} />
                        <FormField control={form.control} name="social_media.linkedin" render={({
                        field
                      }) => <FormItem>
                              <FormLabel className="text-sm">💼 LinkedIn</FormLabel>
                              <FormControl>
                                <Input placeholder="linkedin.com/..." {...field} />
                              </FormControl>
                            </FormItem>} />
                        <FormField control={form.control} name="social_media.whatsapp" render={({
                        field
                      }) => <FormItem>
                              <FormLabel className="text-sm">💬 WhatsApp</FormLabel>
                              <FormControl>
                                <Input placeholder="(00) 00000-0000" {...field} />
                              </FormControl>
                            </FormItem>} />
                        <FormField control={form.control} name="social_media.website" render={({
                        field
                      }) => <FormItem>
                              <FormLabel className="text-sm">🌍 Website</FormLabel>
                              <FormControl>
                                <Input placeholder="www.site.com" {...field} />
                              </FormControl>
                            </FormItem>} />
                        <FormField control={form.control} name="other_social_media" render={({
                        field
                      }) => <FormItem className="col-span-2 md:col-span-3">
                              <FormLabel className="text-sm">🔗 Outras Redes</FormLabel>
                              <FormControl>
                                <Input placeholder="TikTok, YouTube, etc..." {...field} />
                              </FormControl>
                            </FormItem>} />
                      </div>
                      </AgentConfigSection>

                      <AgentConfigSection
                        value="comentarios-instagram"
                        emoji="💬"
                        title="Comentários no Instagram"
                        hint={form.watch("comment_reply_mode") === "always_both" ? "Comentário e DM" : form.watch("comment_reply_mode") === "public_only" ? "Só comentário" : "A IA decide"}
                      >
                        <CommentReplySection
                          replyMode={form.watch("comment_reply_mode")}
                          websitePlacement={form.watch("comment_website_link_placement")}
                          productPlacement={form.watch("comment_product_link_placement")}
                          onReplyModeChange={(value) => form.setValue("comment_reply_mode", value)}
                          onWebsitePlacementChange={(value) => form.setValue("comment_website_link_placement", value)}
                          onProductPlacementChange={(value) => form.setValue("comment_product_link_placement", value)}
                        />
                      </AgentConfigSection>

                      <AgentConfigSection
                        value="horarios"
                        emoji="🕐"
                        title="Horários de Funcionamento"
                        infoKey="horarios_funcionamento"
                        hint={form.watch("restrict_to_business_hours") ? "Restrito ao expediente" : "Sem restrição de horário"}
                      >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField control={form.control} name="timezone" render={({
                        field
                      }) => <FormItem>
                              <FormLabel className="text-sm">Fuso Horário</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selecione o fuso" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="America/Sao_Paulo">🇧🇷 Brasília (GMT-3)</SelectItem>
                                  <SelectItem value="America/Manaus">🇧🇷 Manaus (GMT-4)</SelectItem>
                                  <SelectItem value="America/New_York">🇺🇸 Nova York (GMT-5)</SelectItem>
                                  <SelectItem value="Europe/London">🇬🇧 Londres (GMT+0)</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>} />

                        <FormField control={form.control} name="restrict_to_business_hours" render={({
                        field
                      }) => <FormItem className="flex items-center justify-between rounded-lg border p-3 bg-muted/30">
                              <div className="space-y-0.5">
                                <FormLabel className="text-sm font-medium">🔒 Restringir Horário</FormLabel>
                                <p className="text-xs text-muted-foreground">Não responder fora do horário</p>
                              </div>
                              <FormControl>
                                <Switch checked={field.value} onCheckedChange={field.onChange} />
                              </FormControl>
                            </FormItem>} />
                      </div>

                      <BusinessHoursSelector value={businessHours} onChange={setBusinessHours} />
                      </AgentConfigSection>

                      <AgentConfigSection
                        value="pagamento"
                        emoji="💳"
                        title="Pagamento"
                        infoKey="pagamento"
                        hint={`Modo de preço: ${form.watch("price_disclosure_mode") ?? "livre"}`}
                      >
                      <PaymentSection
                        paymentMethods={form.watch("payment_methods")}
                        onPaymentMethodsChange={(methods) => form.setValue("payment_methods", methods)}
                        installments={form.watch("payment_installments") || ""}
                        onInstallmentsChange={(value) => form.setValue("payment_installments", value)}
                        priceDisclosureMode={form.watch("price_disclosure_mode")}
                        onPriceDisclosureModeChange={(value) => form.setValue("price_disclosure_mode", value)}
                        priceDisclosureCondition={form.watch("price_disclosure_condition") || ""}
                        onPriceDisclosureConditionChange={(value) => form.setValue("price_disclosure_condition", value)}
                      />
                      </AgentConfigSection>

                      <AgentConfigSection
                        value="entrega"
                        emoji="🚚"
                        title="Entrega"
                        infoKey="entrega"
                        hint={form.watch("delivery_enabled") ? "Incluído no prompt" : "Desativado"}
                        headerAction={
                          <Switch
                            checked={form.watch("delivery_enabled")}
                            onCheckedChange={(value) => form.setValue("delivery_enabled", value)}
                          />
                        }
                      >
                      <DeliverySection
                        deliveryInfo={form.watch("delivery_info") || ""}
                        onDeliveryInfoChange={(value) => form.setValue("delivery_info", value)}
                        deliveryEnabled={form.watch("delivery_enabled")}
                        onDeliveryEnabledChange={(value) => form.setValue("delivery_enabled", value)}
                        hideEnableToggle
                      />
                      </AgentConfigSection>

                      <AgentConfigSection
                        value="regras-negocio"
                        emoji="📋"
                        title="Regras do Negócio"
                        infoKey="regras_negocio"
                        hint={`${businessRulesList.filter((r) => r.trim()).length} regra(s)`}
                      >
                      <p className="text-sm text-muted-foreground">
                        Regras que o agente deve SEMPRE seguir rigorosamente
                      </p>

                      <DynamicListInput
                        sortable
                        items={businessRulesList}
                        onChange={setBusinessRulesList}
                        placeholder="Ex: Sempre confirmar dados antes de agendar"
                        addLabel="Adicionar Regra"
                        emptyMessage="Nenhuma regra configurada"
                      />

                      {businessRulesList.length > 0 && <Alert className="bg-orange-500/10 border-orange-500/20">
                          <Info className="h-4 w-4 text-orange-500" />
                          <AlertDescription className="text-xs">
                            Estas regras têm prioridade máxima e serão seguidas rigorosamente.
                          </AlertDescription>
                        </Alert>}
                      </AgentConfigSection>
                    </Accordion>
                  </TabsContent>

                  {/* TAB: COMPORTAMENTO */}
                  <TabsContent value="comportamento" className="mt-0 space-y-4">
                    <Alert className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/50">
                      <Info className="h-4 w-4 text-amber-600 dark:text-amber-500" />
                      <AlertDescription className="text-amber-800 dark:text-amber-200 text-xs">
                        <strong>A IA pode cometer erros.</strong> Revise as conversas regularmente para garantir que o agente está respondendo corretamente e seguindo as configurações definidas.
                      </AlertDescription>
                    </Alert>

                    <Accordion type="multiple" className="space-y-3">
                      <AgentConfigSection
                        value="temperatura"
                        emoji="🌡️"
                        title="Temperatura da IA"
                        infoKey="temperatura_ia"
                        hint={`${Math.round((form.watch("llm_temperature") ?? 0.3) * 100)}% de criatividade`}
                      >
                      <FormField
                        control={form.control}
                        name="llm_temperature"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex items-center justify-between gap-2">
                              <FormLabel className="text-sm font-medium">Criatividade das respostas</FormLabel>
                              <span className="text-xs text-muted-foreground tabular-nums">{Math.round((field.value ?? 0.3) * 100)}%</span>
                            </div>
                            <FormControl>
                              <Slider
                                min={0}
                                max={1}
                                step={0.1}
                                value={[field.value ?? 0.3]}
                                onValueChange={([v]) => field.onChange(v)}
                                className="w-full"
                              />
                            </FormControl>
                            <p className="text-xs text-muted-foreground transition-[color] duration-150">
                              {getCreativityHint(field.value)}
                            </p>
                          </FormItem>
                        )}
                      />
                      </AgentConfigSection>

                      <AgentConfigSection
                        value="permissoes"
                        emoji="⚙️"
                        title="Permissões"
                        infoKey="permissoes"
                        hint={[
                          form.watch("is_active") ? "Agente ativo" : "Agente pausado",
                          form.watch("google_calendar_enabled") ? "Google Calendar" : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      >
                          <FormField control={form.control} name="is_active" render={({
                          field
                        }) => <FormItem className="flex items-center justify-between rounded-lg border p-3 bg-muted/30">
                                <div className="space-y-0.5">
                                  <FormLabel className="text-sm font-medium">⚡ Agente Ativo</FormLabel>
                                  <p className="text-xs text-muted-foreground">Pausar temporariamente</p>
                                </div>
                                <FormControl>
                                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                                </FormControl>
                              </FormItem>} />

                          <FormField control={form.control} name="google_calendar_enabled" render={({
                          field
                        }) => <FormItem className="flex items-center justify-between rounded-lg border p-3 bg-muted/30">
                                <div className="space-y-0.5">
                                  <FormLabel className="text-sm font-medium">📅 Google Calendar</FormLabel>
                                  <p className="text-xs text-muted-foreground">Agendamentos automáticos</p>
                                </div>
                                <FormControl>
                                  <Switch
                                    checked={field.value}
                                    onCheckedChange={(checked) => {
                                      field.onChange(checked);
                                      if (!checked) {
                                        form.setValue("google_calendar_sync_calendar_id", null);
                                        setAllowedCalendarIds([]);
                                      } else if (
                                        !form.getValues("google_calendar_sync_calendar_id") &&
                                        enabledSyncCalendars.length > 0
                                      ) {
                                        const defaultCal =
                                          enabledSyncCalendars.find((sc) => sc.is_primary) ??
                                          enabledSyncCalendars[0];
                                        form.setValue("google_calendar_sync_calendar_id", defaultCal.id);
                                        if (gcalMultiEnabled) {
                                          setAllowedCalendarIds((prev) =>
                                            prev.includes(defaultCal.id) ? prev : [...prev, defaultCal.id],
                                          );
                                        }
                                      }
                                    }}
                                  />
                                </FormControl>
                              </FormItem>} />

                          {form.watch("google_calendar_enabled") && gcalMultiEnabled && (
                            <div className="space-y-3 rounded-lg border border-border p-3 bg-muted/20">
                              <div className="space-y-1">
                                <p className="text-sm font-medium">
                                  Agendas que este agente pode usar
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Selecione até {MAX_AGENT_ALLOWED_CALENDARS} agendas. Agendas não
                                  marcadas não serão usadas.
                                </p>
                              </div>
                              {allowedCalendarsOverLimit && (
                                <Alert className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/50">
                                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                                  <AlertDescription className="text-xs text-amber-800 dark:text-amber-200">
                                    Este agente tem {allowedCalendarIds.length} agendas salvas, mas o
                                    limite atual é {MAX_AGENT_ALLOWED_CALENDARS}. Desmarque as extras
                                    antes de salvar.
                                  </AlertDescription>
                                </Alert>
                              )}
                              <div className="space-y-2 max-h-48 overflow-y-auto rounded-lg border border-border bg-background p-3">
                                {gcalLoading && (
                                  <p className="text-xs text-muted-foreground">Carregando agendas...</p>
                                )}
                                {!gcalLoading && enabledSyncCalendars.length === 0 && (
                                  <p className="text-xs text-muted-foreground">
                                    Nenhuma agenda habilitada em Integrações. Habilite as agendas
                                    desejadas lá e volte aqui.
                                  </p>
                                )}
                                {enabledSyncCalendars.map((sc) => {
                                  const checked = allowedCalendarIds.includes(sc.id);
                                  return (
                                    <div key={sc.id} className="flex items-center gap-2">
                                      <Checkbox
                                        id={`agent-cal-${sc.id}`}
                                        checked={checked}
                                        onCheckedChange={(isChecked) => {
                                          if (isChecked) {
                                            if (allowedCalendarIds.includes(sc.id)) return;
                                            if (
                                              allowedCalendarIds.length >= MAX_AGENT_ALLOWED_CALENDARS
                                            ) {
                                              toast({
                                                title: "Limite de agendas",
                                                description:
                                                  "Por enquanto você pode selecionar no máximo 2 agendas.",
                                              });
                                              return;
                                            }
                                            setAllowedCalendarIds((prev) => [...prev, sc.id]);
                                            if (!form.getValues("google_calendar_sync_calendar_id")) {
                                              form.setValue("google_calendar_sync_calendar_id", sc.id);
                                            }
                                          } else {
                                            if (allowedCalendarIds.length <= 1) {
                                              toast({
                                                title: "Agenda obrigatória",
                                                description:
                                                  "Com Google Calendar ativo, mantenha ao menos uma agenda (a padrão).",
                                              });
                                              return;
                                            }
                                            const next = allowedCalendarIds.filter((id) => id !== sc.id);
                                            setAllowedCalendarIds(next);
                                            if (form.getValues("google_calendar_sync_calendar_id") === sc.id) {
                                              form.setValue(
                                                "google_calendar_sync_calendar_id",
                                                next[0] ?? null,
                                              );
                                            }
                                          }
                                        }}
                                      />
                                      <Label
                                        htmlFor={`agent-cal-${sc.id}`}
                                        className="text-sm font-normal cursor-pointer leading-snug"
                                      >
                                        {sc.display_name || sc.calendar_id}
                                        {sc.is_primary ? " (primária)" : ""}
                                      </Label>
                                    </div>
                                  );
                                })}
                              </div>

                              <FormField
                                control={form.control}
                                name="google_calendar_sync_calendar_id"
                                render={({ field }) => {
                                  const selectedId = field.value ?? null;
                                  const allowedEnabled = enabledSyncCalendars.filter((sc) =>
                                    allowedCalendarIds.includes(sc.id),
                                  );
                                  const selectedDisabled =
                                    selectedId &&
                                    !allowedEnabled.find((sc) => sc.id === selectedId)
                                      ? allSyncCalendars.find((sc) => sc.id === selectedId)
                                      : null;
                                  const servicesWithOwn = linkedServiceCalendars.filter(
                                    (s) => s.google_calendar_sync_calendar_id,
                                  );
                                  const servicesInherit = linkedServiceCalendars.filter(
                                    (s) => !s.google_calendar_sync_calendar_id,
                                  );
                                  const servicesOutsideAllowlist = servicesWithOwn.filter(
                                    (s) =>
                                      s.google_calendar_sync_calendar_id &&
                                      !allowedCalendarIds.includes(s.google_calendar_sync_calendar_id),
                                  );
                                  const showMultiServiceWarning =
                                    allowedCalendarIds.length >= 2 &&
                                    linkedServiceCalendars.length >= 2 &&
                                    servicesInherit.length >= 2;

                                  const nameCounts = new Map<string, number>();
                                  for (const s of linkedServiceCalendars) {
                                    const key = s.name.trim().toLowerCase();
                                    nameCounts.set(key, (nameCounts.get(key) ?? 0) + 1);
                                  }
                                  const ambiguousNames = [...nameCounts.entries()]
                                    .filter(([, n]) => n > 1)
                                    .map(([name]) => name);

                                  return (
                                    <FormItem>
                                      <FormLabel className="text-sm">
                                        Agenda padrão (fallback)
                                      </FormLabel>
                                      <Select
                                        onValueChange={field.onChange}
                                        value={field.value ?? undefined}
                                        disabled={
                                          gcalLoading ||
                                          (allowedEnabled.length === 0 && !selectedDisabled)
                                        }
                                      >
                                        <FormControl>
                                          <SelectTrigger>
                                            <SelectValue
                                              placeholder={
                                                gcalLoading
                                                  ? "Carregando agendas..."
                                                  : allowedEnabled.length === 0
                                                    ? "Marque ao menos uma agenda acima"
                                                    : "Selecione a agenda padrão"
                                              }
                                            />
                                          </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                          {selectedDisabled && (
                                            <SelectItem value={selectedDisabled.id}>
                                              {selectedDisabled.display_name ||
                                                selectedDisabled.calendar_id}
                                              {" (fora do allowlist / desabilitada)"}
                                            </SelectItem>
                                          )}
                                          {allowedEnabled.map((sc) => (
                                            <SelectItem key={sc.id} value={sc.id}>
                                              {sc.display_name || sc.calendar_id}
                                              {sc.is_primary ? " (primária)" : ""}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                      <p className="text-xs text-muted-foreground">
                                        Usada quando o serviço não tem agenda própria. Só lista as agendas
                                        marcadas acima. Para profissionais diferentes, defina a agenda em
                                        cada{" "}
                                        <Link
                                          to="/products"
                                          className="underline underline-offset-2 text-foreground/80 hover:text-foreground"
                                          onClick={() => onOpenChange(false)}
                                        >
                                          Serviço
                                        </Link>
                                        .
                                      </p>
                                      {servicesOutsideAllowlist.length > 0 && (
                                        <Alert className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/50 mt-2">
                                          <AlertTriangle className="h-4 w-4 text-amber-600" />
                                          <AlertDescription className="text-xs text-amber-800 dark:text-amber-200">
                                            {servicesOutsideAllowlist.length} serviço(s) vinculado(s) apontam
                                            para agenda fora do allowlist (
                                            {servicesOutsideAllowlist
                                              .slice(0, 3)
                                              .map((s) => s.name)
                                              .join(", ")}
                                            ). Disponibilidade/create falharão até corrigir o serviço ou
                                            incluir a agenda aqui.
                                          </AlertDescription>
                                        </Alert>
                                      )}
                                      {linkedServiceCalendars.length > 0 && (
                                        <p className="text-xs text-muted-foreground">
                                          Serviços com agenda própria: {servicesWithOwn.length}
                                          {servicesInherit.length > 0
                                            ? ` · Herdam a padrão: ${servicesInherit.length}`
                                            : ""}
                                        </p>
                                      )}
                                      {showMultiServiceWarning && (
                                        <Alert className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/50 mt-2">
                                          <AlertTriangle className="h-4 w-4 text-amber-600" />
                                          <AlertDescription className="text-xs text-amber-800 dark:text-amber-200">
                                            Há {allowedCalendarIds.length} agendas permitidas e{" "}
                                            {servicesInherit.length} serviços sem agenda própria — todos
                                            herdam a padrão. Se cada profissional/equipe tiver agenda
                                            diferente, configure em{" "}
                                            <Link
                                              to="/products"
                                              className="underline underline-offset-2 font-medium"
                                              onClick={() => onOpenChange(false)}
                                            >
                                              Produtos/Serviços
                                            </Link>
                                            .
                                          </AlertDescription>
                                        </Alert>
                                      )}
                                      {ambiguousNames.length > 0 && (
                                        <Alert className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/50 mt-2">
                                          <AlertTriangle className="h-4 w-4 text-amber-600" />
                                          <AlertDescription className="text-xs text-amber-800 dark:text-amber-200">
                                            Há serviços com nomes iguais ou muito parecidos (
                                            {ambiguousNames.slice(0, 3).join(", ")}). Isso pode confundir o
                                            matching no agendamento — use nomes distintos (ex.: “Consulta Dr.
                                            A”).
                                          </AlertDescription>
                                        </Alert>
                                      )}
                                      <FormMessage />
                                    </FormItem>
                                  );
                                }}
                              />
                            </div>
                          )}

                          {form.watch("google_calendar_enabled") && !gcalMultiEnabled && (
                            <FormField
                              control={form.control}
                              name="google_calendar_sync_calendar_id"
                              render={({ field }) => {
                                const selectedId = field.value ?? null;
                                const selectedDisabled =
                                  selectedId &&
                                  !enabledSyncCalendars.find((sc) => sc.id === selectedId)
                                    ? allSyncCalendars.find((sc) => sc.id === selectedId)
                                    : null;
                                return (
                                  <FormItem>
                                    <FormLabel className="text-sm">
                                      Agenda usada por este agente
                                    </FormLabel>
                                    <Select
                                      onValueChange={field.onChange}
                                      value={field.value ?? undefined}
                                      disabled={
                                        gcalLoading ||
                                        (enabledSyncCalendars.length === 0 && !selectedDisabled)
                                      }
                                    >
                                      <FormControl>
                                        <SelectTrigger>
                                          <SelectValue
                                            placeholder={
                                              gcalLoading
                                                ? "Carregando agendas..."
                                                : enabledSyncCalendars.length === 0
                                                  ? "Nenhuma agenda habilitada"
                                                  : "Selecione a agenda"
                                            }
                                          />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        {selectedDisabled && (
                                          <SelectItem value={selectedDisabled.id}>
                                            {selectedDisabled.display_name ||
                                              selectedDisabled.calendar_id}
                                            {" (desabilitada)"}
                                          </SelectItem>
                                        )}
                                        {enabledSyncCalendars.map((sc) => (
                                          <SelectItem key={sc.id} value={sc.id}>
                                            {sc.display_name || sc.calendar_id}
                                            {sc.is_primary ? " (primária)" : ""}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <p className="text-xs text-muted-foreground">
                                      Horários ocupados e novos agendamentos usam esta agenda.
                                    </p>
                                    <FormMessage />
                                  </FormItem>
                                );
                              }}
                            />
                          )}

                          {form.watch("google_calendar_enabled") && <>
                              <FormField control={form.control} name="can_schedule" render={({
                            field
                          }) => <FormItem className="flex items-center justify-between rounded-lg border p-3">
                                    <FormLabel className="text-sm">✅ Pode Agendar</FormLabel>
                                    <FormControl>
                                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                                    </FormControl>
                                  </FormItem>} />

                              <FormField control={form.control} name="can_reschedule" render={({
                            field
                          }) => <FormItem className="flex items-center justify-between rounded-lg border p-3">
                                    <FormLabel className="text-sm">🔄 Pode Remarcar</FormLabel>
                                    <FormControl>
                                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                                    </FormControl>
                                  </FormItem>} />

                              <FormField control={form.control} name="can_cancel" render={({
                            field
                          }) => <FormItem className="flex items-center justify-between rounded-lg border p-3">
                                    <FormLabel className="text-sm">❌ Pode Cancelar</FormLabel>
                                    <FormControl>
                                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                                    </FormControl>
                                  </FormItem>} />

                              <FormField control={form.control} name="appointment_duration" render={({
                            field
                          }) => <FormItem>
                                    <FormLabel className="text-sm">⏱️ Duração Padrão</FormLabel>
                                    <Select onValueChange={value => field.onChange(parseInt(value))} value={field.value.toString()}>
                                      <FormControl>
                                        <SelectTrigger>
                                          <SelectValue />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        <SelectItem value="15">15 minutos</SelectItem>
                                        <SelectItem value="30">30 minutos</SelectItem>
                                        <SelectItem value="45">45 minutos</SelectItem>
                                        <SelectItem value="60">1 hora</SelectItem>
                                        <SelectItem value="90">1h30</SelectItem>
                                        <SelectItem value="120">2 horas</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </FormItem>} />

                              <FormField control={form.control} name="max_appointment_options" render={({
                            field
                          }) => <FormItem>
                                    <FormLabel className="text-sm">📋 Máximo de horários a propor</FormLabel>
                                    <Select onValueChange={value => field.onChange(parseInt(value))} value={String(field.value ?? 3)}>
                                      <FormControl>
                                        <SelectTrigger>
                                          <SelectValue />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        <SelectItem value="1">1 horário</SelectItem>
                                        <SelectItem value="2">2 horários</SelectItem>
                                        <SelectItem value="3">3 horários</SelectItem>
                                        <SelectItem value="4">4 horários</SelectItem>
                                        <SelectItem value="5">5 horários</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <p className="text-xs text-muted-foreground">Quantos horários alternativos a IA oferece por vez ao cliente.</p>
                                  </FormItem>} />

                              <FormField control={form.control} name="scheduling_start_offset_days" render={({
                            field
                          }) => <FormItem>
                                    <FormLabel className="text-sm">⏳ Antecedência mínima (dias)</FormLabel>
                                    <Select onValueChange={value => field.onChange(parseInt(value))} value={String(field.value ?? 0)}>
                                      <FormControl>
                                        <SelectTrigger>
                                          <SelectValue />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent className="max-h-[200px]">
                                        {Array.from({ length: 61 }).map((_, i) => (
                                          <SelectItem key={i} value={String(i)}>
                                            {i} {i === 1 ? "dia" : "dias"}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <p className="text-xs text-muted-foreground">0 = pode oferecer para hoje. 1 = só a partir de amanhã.</p>
                                  </FormItem>} />

                              <FormField control={form.control} name="scheduling_search_days" render={({
                            field
                          }) => <FormItem>
                                    <FormLabel className="text-sm">📆 Dias ofertados por vez</FormLabel>
                                    <Select onValueChange={value => field.onChange(parseInt(value))} value={String(field.value ?? 5)}>
                                      <FormControl>
                                        <SelectTrigger>
                                          <SelectValue />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent className="max-h-[200px]">
                                        {Array.from({ length: 15 }).map((_, i) => {
                                          const val = i + 1;
                                          return (
                                            <SelectItem key={val} value={String(val)}>
                                              {val} {val === 1 ? "dia" : "dias"}
                                            </SelectItem>
                                          );
                                        })}
                                      </SelectContent>
                                    </Select>
                                    <p className="text-xs text-muted-foreground">Quantos dias úteis a IA percorre buscando horários livres.</p>
                                  </FormItem>} />
                            </>}
                      </AgentConfigSection>

                      <AgentConfigSection
                        value="follow-up"
                        emoji="🔄"
                        title="Follow-up Automático"
                        infoKey="follow_up"
                        infoClassName="right-14 sm:right-16"
                        hint={followUpEnabled ? "Ativo" : "Desativado"}
                        headerAction={
                          <Switch checked={followUpEnabled} onCheckedChange={setFollowUpEnabled} />
                        }
                      >
                          {followUpEnabled && <div className="grid grid-cols-2 gap-4 pt-2">
                              <div className="space-y-2">
                                <label className="text-sm font-medium">Intervalo</label>
                                <Select value={followUpInterval.toString()} onValueChange={(v) => setFollowUpInterval(parseInt(v))}>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="5">5 minutos</SelectItem>
                                    <SelectItem value="15">15 minutos</SelectItem>
                                    <SelectItem value="30">30 minutos</SelectItem>
                                    <SelectItem value="60">1 hora</SelectItem>
                                    <SelectItem value="120">2 horas</SelectItem>
                                    <SelectItem value="240">4 horas</SelectItem>
                                    <SelectItem value="480">8 horas</SelectItem>
                                    <SelectItem value="1440">24 horas</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <label className="text-sm font-medium">Máximo de tentativas</label>
                                <Select value={followUpMaxAttempts.toString()} onValueChange={(v) => setFollowUpMaxAttempts(parseInt(v))}>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="1">1 tentativa</SelectItem>
                                    <SelectItem value="2">2 tentativas</SelectItem>
                                    <SelectItem value="3">3 tentativas</SelectItem>
                                    <SelectItem value="4">4 tentativas</SelectItem>
                                    <SelectItem value="5">5 tentativas</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>}

                          {!followUpEnabled && <p className="text-sm text-muted-foreground">
                              Quando ativado, o agente enviará mensagens automáticas caso o cliente não responda.
                            </p>}
                      </AgentConfigSection>

                      <AgentConfigSection
                        value="catalogo"
                        emoji="📄"
                        title="Catálogo / Cardápio"
                        infoKey="catalogo"
                        infoClassName="right-14 sm:right-16"
                        hint={catalogEnabled ? "Ativo" : "Desativado"}
                        headerAction={
                          <Switch checked={catalogEnabled} onCheckedChange={setCatalogEnabled} />
                        }
                      >
                          {catalogEnabled ? (
                            <div className="pt-3 space-y-3">
                              <p className="text-sm text-muted-foreground">
                                Escolha como o agente envia o catálogo quando o cliente pedir: arquivo PDF ou link do site.
                                Para políticas, manuais e procedimentos, use a aba Negócio → Base de conhecimento.
                              </p>

                              <div className="space-y-2 rounded-lg border border-dashed p-3 bg-muted/20">
                                <Label className="text-sm font-medium">Palavras-chave extras (opcional)</Label>
                                <p className="text-xs text-muted-foreground">
                                  Catálogo, cardápio e lista continuam funcionando como hoje. Aqui você adiciona termos do seu
                                  negócio (ex.: &quot;tabela de preços&quot;, &quot;folder&quot;) que também disparam o envio automático.
                                </p>
                                <DynamicListInput
                                  items={catalogTriggerPhrases}
                                  onChange={setCatalogTriggerPhrases}
                                  placeholder="Ex.: tabela de preços, folder"
                                  addLabel="Adicionar palavra-chave"
                                  emptyMessage="Nenhuma extra, apenas os gatilhos padrão do sistema"
                                  maxItems={MAX_CATALOG_TRIGGER_PHRASES}
                                />
                              </div>

                              <RadioGroup
                                value={catalogStorageProvider === "link" ? "link" : "pdf"}
                                onValueChange={(value) => {
                                  if (value === "link") {
                                    const hadPdfFile =
                                      Boolean(catalogObjectKey) || catalogStorageProvider === "minio";
                                    setCatalogStorageProvider("link");
                                    setCatalogBucket(null);
                                    setCatalogObjectKey(null);
                                    setCatalogMimeType(null);
                                    setCatalogFileSize(null);
                                    setCatalogPublicUrl(null);
                                    setCatalogFileName("Catálogo (link)");
                                    // Mantém URL se já era link; limpa se era PDF legado/minio
                                    if (hadPdfFile) {
                                      setCatalogFileUrl(null);
                                    }
                                  } else if (catalogStorageProvider === "link") {
                                    // Volta para PDF: limpa link externo
                                    setCatalogFileUrl(null);
                                    setCatalogFileName(null);
                                    setCatalogStorageProvider(null);
                                  }
                                }}
                                className="grid grid-cols-2 gap-2"
                              >
                                <Label
                                  htmlFor="catalog-mode-pdf"
                                  className={cn(
                                    "flex items-center gap-2 rounded-lg border p-3 cursor-pointer transition-colors",
                                    catalogStorageProvider !== "link"
                                      ? "border-primary bg-primary/5"
                                      : "border-border hover:bg-muted/40"
                                  )}
                                >
                                  <RadioGroupItem value="pdf" id="catalog-mode-pdf" />
                                  <FileText className="w-4 h-4 text-primary" />
                                  <span className="text-sm font-medium">PDF</span>
                                </Label>
                                <Label
                                  htmlFor="catalog-mode-link"
                                  className={cn(
                                    "flex items-center gap-2 rounded-lg border p-3 cursor-pointer transition-colors",
                                    catalogStorageProvider === "link"
                                      ? "border-primary bg-primary/5"
                                      : "border-border hover:bg-muted/40"
                                  )}
                                >
                                  <RadioGroupItem value="link" id="catalog-mode-link" />
                                  <Link2 className="w-4 h-4 text-primary" />
                                  <span className="text-sm font-medium">Link</span>
                                </Label>
                              </RadioGroup>

                              {catalogStorageProvider === "link" ? (
                                <div className="space-y-2">
                                  <Label htmlFor="catalog-link-url" className="text-sm">
                                    URL do catálogo
                                  </Label>
                                  <Input
                                    id="catalog-link-url"
                                    type="url"
                                    placeholder="https://seusite.com/catalogo"
                                    value={catalogFileUrl || ""}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setCatalogFileUrl(value || null);
                                      setCatalogStorageProvider("link");
                                      setCatalogFileName(value ? "Catálogo (link)" : null);
                                      setCatalogBucket(null);
                                      setCatalogObjectKey(null);
                                      setCatalogMimeType(null);
                                      setCatalogFileSize(null);
                                      setCatalogPublicUrl(null);
                                    }}
                                  />
                                  <p className="text-xs text-muted-foreground">
                                    O agente enviará esta URL para o lead clicar e abrir o site.
                                  </p>
                                </div>
                              ) : catalogFileUrl || catalogObjectKey ? (
                                <div className="flex items-center justify-between rounded-lg border p-3 bg-muted/30">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <FileText className="w-4 h-4 text-primary flex-shrink-0" />
                                    <span className="text-sm truncate">{catalogFileName || 'catalog.pdf'}</span>
                                    {catalogFileSize ? (
                                      <span className="text-xs text-muted-foreground flex-shrink-0">
                                        ({(catalogFileSize / 1024 / 1024).toFixed(1)} MB)
                                      </span>
                                    ) : null}
                                  </div>
                                  <Button type="button" variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground hover:bg-muted/80" onClick={() => {
                                    // Limpa apenas referências locais; arquivo no MinIO é sobrescrito no próximo upload
                                    setCatalogFileUrl(null);
                                    setCatalogFileName(null);
                                    setCatalogStorageProvider(null);
                                    setCatalogBucket(null);
                                    setCatalogObjectKey(null);
                                    setCatalogMimeType(null);
                                    setCatalogFileSize(null);
                                    setCatalogPublicUrl(null);
                                  }}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  <label
                                    htmlFor="catalog-upload"
                                    className={cn(
                                      "flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 cursor-pointer transition-colors",
                                      "hover:border-primary/50 hover:bg-muted/50",
                                      (isUploadingCatalog || !agent?.id) && "opacity-50 pointer-events-none"
                                    )}
                                  >
                                    <FileUp className="w-8 h-8 text-muted-foreground mb-2" />
                                    <span className="text-sm text-muted-foreground text-center">
                                      {!agent?.id
                                        ? "Salve o agente antes de enviar o catálogo"
                                        : isUploadingCatalog
                                          ? "Enviando para o servidor..."
                                          : "Clique para enviar PDF (até 16 MB)"}
                                    </span>
                                  </label>
                                  <input
                                    id="catalog-upload"
                                    type="file"
                                    accept=".pdf"
                                    className="hidden"
                                    disabled={!agent?.id}
                                    onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;
                                      if (file.type !== 'application/pdf') {
                                        toast({ title: "Formato inválido", description: "Apenas PDF é aceito", variant: "destructive" });
                                        e.target.value = '';
                                        return;
                                      }
                                      if (file.size > 16 * 1024 * 1024) {
                                        toast({ title: "Arquivo muito grande", description: "WhatsApp aceita documentos de até 16 MB.", variant: "destructive" });
                                        e.target.value = '';
                                        return;
                                      }
                                      if (file.size > 8 * 1024 * 1024) {
                                        toast({ title: "Arquivo grande", description: "O envio pode demorar um pouco." });
                                      }
                                      if (!agent?.id) {
                                        toast({ title: "Salve o agente primeiro", description: "Crie o agente antes de anexar o catálogo.", variant: "destructive" });
                                        return;
                                      }
                                      setIsUploadingCatalog(true);
                                      try {
                                        // 1. Pede URL presigned ao MinIO via edge function
                                        const { data: presign, error: presignErr } = await supabase.functions.invoke(
                                          "presign-catalog-upload",
                                          {
                                            body: {
                                              workspace_id: workspaceId,
                                              agent_id: agent.id,
                                              file_name: file.name,
                                              mime_type: "application/pdf",
                                              size: file.size,
                                            },
                                          }
                                        );
                                        if (presignErr || !presign?.upload_url) {
                                          throw new Error(presignErr?.message || presign?.error || "Falha ao obter URL de upload");
                                        }

                                        // 2. PUT direto no MinIO
                                        const putResp = await fetch(presign.upload_url, {
                                          method: "PUT",
                                          headers: presign.headers || { "Content-Type": "application/pdf" },
                                          body: file,
                                        });
                                        if (!putResp.ok) {
                                          const txt = await putResp.text().catch(() => "");
                                          throw new Error(`Upload ao MinIO falhou (${putResp.status}): ${txt.slice(0, 200)}`);
                                        }

                                        // 3. Salva metadados no estado (persistidos no submit)
                                        setCatalogStorageProvider("minio");
                                        setCatalogBucket(presign.bucket || null);
                                        setCatalogObjectKey(presign.object_key);
                                        setCatalogFileName(presign.file_name || file.name);
                                        setCatalogMimeType("application/pdf");
                                        setCatalogFileSize(file.size);
                                        setCatalogPublicUrl(presign.public_url || null);
                                        setCatalogFileUrl(null); // não usar URL legada quando temos object_key
                                        toast({ title: "Catálogo enviado", description: "Arquivo armazenado no servidor com sucesso." });
                                      } catch (err: any) {
                                        toast({ title: "Erro no upload", description: err.message, variant: "destructive" });
                                      } finally {
                                        setIsUploadingCatalog(false);
                                        e.target.value = '';
                                      }
                                    }}
                                  />
                                </div>
                              )}

                              <Alert className="bg-blue-500/10 border-blue-500/20">
                                <Info className="h-4 w-4 text-blue-500" />
                                <AlertDescription className="text-xs">
                                  {catalogStorageProvider === "link"
                                    ? "O agente enviará o link quando o cliente pedir catálogo, cardápio, menu ou lista de produtos. O lead clica e abre o site."
                                    : "O agente enviará este arquivo quando o cliente pedir catálogo, cardápio, menu ou lista de produtos. No Instagram, será enviado como link."}
                                </AlertDescription>
                              </Alert>
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              Ative para permitir que o agente envie o catálogo como PDF ou como link do site.
                            </p>
                          )}
                      </AgentConfigSection>

                      <AgentConfigSection
                        value="lembrete"
                        emoji="🔔"
                        title="Lembrete de Agendamento"
                        infoKey="lembrete_agendamento"
                        hint="Configurar em Integrações → Google Calendar"
                      >
                        <Alert className="bg-muted/50">
                          <Info className="h-4 w-4" />
                          <AlertDescription className="text-sm">
                            Configure os lembretes de agendamento em{" "}
                            <strong>Integrações → Google Calendar → Configurar Eventos</strong>.
                            <br />
                            <span className="text-muted-foreground mt-1 block">
                              Lá você pode definir os tempos de lembrete (ex: 24h antes, 2h antes), 
                              canal de envio (WhatsApp/Email) e mensagem personalizada.
                            </span>
                          </AlertDescription>
                        </Alert>
                      </AgentConfigSection>

                      <AgentConfigSection
                        value="transferencia-humano"
                        emoji="🤝"
                        title="Transferência para Humano"
                        infoKey="transferencia_humano"
                        infoClassName="right-14 sm:right-16"
                        hint={humanHandoffEnabled ? "Ativo" : "Desativado"}
                        headerAction={
                          <Switch
                            checked={humanHandoffEnabled}
                            onCheckedChange={(checked) => {
                              setHumanHandoffEnabled(checked);
                              if (checked && humanHandoffRulesV2.length === 0) {
                                setHumanHandoffRulesV2([
                                  { condition: "Transfira para humano somente quando o cliente pedir explicitamente para falar com uma pessoa, humano, atendente, consultor ou responsável. Não transfira apenas porque ele relatou uma dor, dúvida comercial ou necessidade do negócio.", target_funnel_id: null, target_stage_slug: "atendimento_humano" },
                                  { condition: "Transfira para humano somente quando o cliente fizer uma reclamação grave, citar cobrança indevida, erro operacional, cancelamento, ameaça jurídica ou problema urgente que exija intervenção humana.", target_funnel_id: null, target_stage_slug: "atendimento_humano" },
                                  { condition: "Transfira para humano quando a dúvida envolver contrato, pagamento, dados sensíveis, negociação especial ou decisão comercial que não esteja nas informações cadastradas do agente.", target_funnel_id: null, target_stage_slug: "atendimento_humano" }
                                ]);
                              }
                            }}
                          />
                        }
                      >
                        {humanHandoffEnabled ? (
                          <div className="space-y-3">
                            <p className="text-sm text-muted-foreground">
                              Defina quando o agente deve transferir e para qual funil/estágio mover o lead
                            </p>

                            <HandoffRuleInput
                              rules={humanHandoffRulesV2}
                              onChange={setHumanHandoffRulesV2}
                              workspaceId={workspaceId}
                              maxItems={10}
                            />

                            <Alert className="bg-orange-500/10 border-orange-500/20">
                              <Info className="h-4 w-4 text-orange-500" />
                              <AlertDescription className="text-xs">
                                Quando acionada, a IA moverá o lead para o funil e estágio configurados na regra e pausará as respostas automáticas.
                              </AlertDescription>
                            </Alert>
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            O agente tentará resolver tudo sozinho, sem transferir para humanos.
                          </p>
                        )}
                      </AgentConfigSection>

                      <AgentConfigSection
                        value="escalacao"
                        emoji="✨"
                        title="Escalação para outro agente"
                        infoKey="escalacao_agente"
                        hint={
                          agentEscalationRulesV2.length > 0
                            ? `${agentEscalationRulesV2.length} regra(s) configurada(s)`
                            : "Nenhuma regra configurada"
                        }
                      >
                        <div className="space-y-3">
                            <p className="text-sm text-muted-foreground">
                              Defina quando este agente deve transferir o atendimento para outro agente IA do workspace.
                              O agente destino continuará respondendo na mesma conversa.
                            </p>
                            <AgentEscalationRuleInput
                              rules={agentEscalationRulesV2}
                              onChange={setAgentEscalationRulesV2}
                              workspaceId={workspaceId}
                              currentAgentId={agent?.id}
                              maxItems={10}
                            />
                            <Alert className="bg-violet-500/10 border-violet-500/20">
                              <Info className="h-4 w-4 text-violet-500" />
                              <AlertDescription className="text-xs">
                                Diferente do handoff humano: a conversa permanece ativa e o agente especialista responde automaticamente.
                              </AlertDescription>
                            </Alert>
                        </div>
                      </AgentConfigSection>

                      <AgentConfigSection
                        value="produtos"
                        emoji="📦"
                        title="Produtos Associados"
                        infoKey="produtos_associados"
                        hint={`${selectedProductIds.length} produto(s) selecionado(s)`}
                      >
                          <p className="text-sm text-muted-foreground mb-4">
                            Selecione os produtos que este agente pode oferecer
                            {gcalMultiEnabled && form.watch("google_calendar_enabled")
                              ? ". Para agendas por profissional, configure a agenda em cada serviço em Produtos."
                              : ""}
                          </p>
                          <AgentProductSelector
                            selectedProductIds={selectedProductIds}
                            onSelectionChange={setSelectedProductIds}
                            workspaceId={workspaceId}
                            agentId={agent?.id}
                          />
                      </AgentConfigSection>
                    </Accordion>
                  </TabsContent>
                </div>
              </Tabs>

              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between px-4 sm:px-6 py-3 sm:py-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:pb-4 border-t bg-background flex-shrink-0">
                <div className="flex items-center gap-0.5 order-2 sm:order-1 w-full sm:w-auto justify-center sm:justify-start">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowPromptPreview(true)}
                    className="text-muted-foreground text-sm flex-1 sm:flex-initial justify-center sm:justify-start"
                  >
                    <Eye className="w-4 h-4 mr-2 flex-shrink-0" /> Preview do Agente
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 shrink-0 text-muted-foreground"
                        aria-label="Importar e exportar configurações"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" side="top" className="w-56">
                      <DropdownMenuItem onClick={() => void handleCopyConfig()}>
                        <Copy className="mr-2 h-4 w-4" />
                        Copiar campos e valores
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExportConfig("csv")}>
                        <Download className="mr-2 h-4 w-4" />
                        Exportar CSV
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExportConfig("xlsx")}>
                        <Download className="mr-2 h-4 w-4" />
                        Exportar Excel (XLSX)
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleDownloadTemplate("csv")}>
                        <FileSpreadsheet className="mr-2 h-4 w-4" />
                        Template CSV
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDownloadTemplate("xlsx")}>
                        <FileSpreadsheet className="mr-2 h-4 w-4" />
                        Template Excel (XLSX)
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setImportConfigOpen(true)}>
                        <Upload className="mr-2 h-4 w-4" />
                        Importar CSV / XLSX
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="flex gap-2 sm:gap-3 w-full sm:w-auto order-1 sm:order-2">
                  <Button type="button" variant="outline" onClick={requestClose} className="flex-1 sm:flex-initial min-w-0">
                    Cancelar
                  </Button>
                  <Button type="submit" className="flex-1 sm:flex-initial min-w-0 sm:min-w-[100px] bg-agent-primary hover:bg-agent-primary-light">
                    {agent ? "Atualizar" : "Criar"}
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Prompt Preview Modal */}
      <Dialog open={showPromptPreview} onOpenChange={setShowPromptPreview}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Preview do Prompt
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[60vh]">
            <div className="space-y-4 pr-4">
              {agentInstructions.length > 0 && <div className="bg-agent-accent/10 border border-agent-accent/30 rounded-lg p-4">
                  <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" />
                    Instruções do Agente
                  </h4>
                  <ol className="list-decimal list-inside space-y-1 text-sm">
                    {agentInstructions.filter(i => i.trim()).map((i, idx) => <li key={idx}>{i}</li>)}
                  </ol>
                </div>}

              {qualificationQuestions.length > 0 && <div className="bg-agent-primary/5 border border-agent-primary/20 rounded-lg p-4">
                  <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                    <HelpCircle className="w-4 h-4" />
                    Perguntas de Qualificação
                  </h4>
                  <ol className="list-decimal list-inside space-y-1 text-sm">
                    {qualificationQuestions.filter(q => q.trim()).map((q, i) => <li key={i}>{q}</li>)}
                  </ol>
                </div>}

              {businessRulesList.length > 0 && <div className="bg-orange-500/5 border border-orange-500/20 rounded-lg p-4">
                  <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Regras de Negócio
                  </h4>
                  <ol className="list-decimal list-inside space-y-1 text-sm">
                    {businessRulesList.filter(r => r.trim()).map((r, i) => <li key={i}>{r}</li>)}
                  </ol>
                </div>}

              <div className="bg-muted rounded-lg p-4">
                <h4 className="font-semibold text-sm mb-2">Configurações do Agente</h4>
                <div className="text-sm space-y-1 text-muted-foreground">
                  <p><strong>Nome:</strong> {form.watch("display_name") || form.watch("name")}</p>
                  <p><strong>Função:</strong> {form.watch("role")}</p>
                  <p><strong>Tom:</strong> {form.watch("tone")}</p>
                  <p><strong>Idioma:</strong> {form.watch("language")}</p>
                  {form.watch("google_calendar_enabled") && <>
                      {gcalMultiEnabled && selectedSyncCalendarLabel && (
                        <p><strong>Agenda Google:</strong> {selectedSyncCalendarLabel}</p>
                      )}
                      <p><strong>Pode Agendar:</strong> {form.watch("can_schedule") ? "Sim" : "Não"}</p>
                      <p><strong>Pode Remarcar:</strong> {form.watch("can_reschedule") ? "Sim" : "Não"}</p>
                      <p><strong>Pode Cancelar:</strong> {form.watch("can_cancel") ? "Sim" : "Não"}</p>
                    </>}
                </div>
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <AgentConfigImportDialog
        open={importConfigOpen}
        onOpenChange={setImportConfigOpen}
        currentSnapshot={buildConfigSnapshot()}
        onApply={applyImportedSnapshot}
      />

      <AlertDialog open={unsavedCloseOpen} onOpenChange={setUnsavedCloseOpen}>
        <AlertDialogContent className="z-[100]">
          <AlertDialogHeader>
            <AlertDialogTitle>Alterações não salvas</AlertDialogTitle>
            <AlertDialogDescription>
              Existem alterações que ainda não foram salvas. Se fechar agora, elas serão perdidas. Deseja continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDiscardAndClose}
            >
              Descartar e fechar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>;
}